import React, { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../hooks/useAuth'
import { EonLogo } from '../../components/shared/EonLogo'
import { KATEGORI_FARGER, AVDELNING_FARGER, beraknaSkiftersattning, beraknaRegistreradeTimmar, basPassNamn } from '../../lib/constants'
import { hamtaHelgdagar } from '../../lib/helgdagar'
import { BekraftaModal } from '../../components/BekraftaModal'

const AVDELNINGAR = ['RN', 'LN MMÖ', 'LN NKP', 'DISP']
// Dagens datum enligt lokal tid (hämtas varje gång, så det stämmer även strax efter midnatt)
function idagLokal() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// Oläst i inkorgen: ej sedd, ej arkiverad. Egna ansökningar räknas först när de fått svar,
// men inkommande förfrågningar till mig (t.ex. passbyte) räknas direkt tills jag öppnat inkorgen.
const arOlast = a => !a.sedd && !a.arkiverad_medarbetare && (a.status !== 'väntar' || a.typ === 'passandring_notis')

// Maskar tid medan man skriver: siffror blir automatiskt "HH:MM"
function maskaTid(varde) {
  const siffror = varde.replace(/\D/g, '').slice(0, 4)
  if (siffror.length <= 2) return siffror
  return siffror.slice(0, 2) + ':' + siffror.slice(2)
}

// Validerar att en maskad tid ("HH:MM") är en riktig klockslag
function arGiltigTid(tid) {
  const match = (tid || '').match(/^(\d{2}):(\d{2})$/)
  if (!match) return false
  return Number(match[1]) <= 23 && Number(match[2]) <= 59
}

// SF, SD osv → semester + pass, räkna timmarna för passet
function tolkPassNamn(namn) {
  if (!namn) return { visningsnamn: namn, erSemester: false, basPass: namn }
  const u = namn.toUpperCase()
  if (u.startsWith('SJ') && u.length > 2) {
    return { visningsnamn: namn, erSjukdom: true, basPass: u.slice(2) }
  }
  if (u.startsWith('S') && u.length >= 1) {
    return { visningsnamn: namn, erSemester: true, basPass: u.slice(1) }
  }
  if (u.startsWith('L') && u.length > 1) {
    return { visningsnamn: namn, erLedig: true, basPass: u.slice(1) }
  }
  return { visningsnamn: namn, erSemester: false, basPass: u }
}

// Supabase begränsar varje enskild fråga till max 1000 rader på server-
// sidan, oavsett vad man ber om i .limit() - det går inte att kringgå med
// en högre .limit()-siffra. Enda pålitliga lösningen är att hämta i sidor
// om 1000 tills en sida kommer tillbaka mindre än 1000.
async function hamtaAllaSidor(queryBuilder) {
  let allaRader = []
  let sida = 0
  const SIDSTORLEK = 1000
  while (true) {
    // id som sista sortering: utan fast ordning kan rader hoppa över eller dubbleras mellan sidorna
    const { data, error } = await queryBuilder().order('id', { ascending: true }).range(sida * SIDSTORLEK, sida * SIDSTORLEK + SIDSTORLEK - 1)
    if (error) { console.error('Fel vid sidhämtning:', error); break }
    allaRader = allaRader.concat(data || [])
    if (!data || data.length < SIDSTORLEK) break
    sida++
  }
  return allaRader
}

// ISO-veckonummer för ett datum
function isoVeckaNr(ar, manad0, dag) {
  const d = new Date(Date.UTC(ar, manad0, dag))
  const dagNr = (d.getUTCDay() + 6) % 7
  d.setUTCDate(d.getUTCDate() - dagNr + 3)
  const forstaTors = new Date(Date.UTC(d.getUTCFullYear(), 0, 4))
  return 1 + Math.round(((d - forstaTors) / 86400000 - 3 + ((forstaTors.getUTCDay() + 6) % 7)) / 7)
}

// Mörkt läge: sparas per enhet i webbläsaren och läggs som attribut på <html>
function lasMorktLage() {
  try { return localStorage.getItem('eon_tema') === 'mork' } catch { return false }
}
function sattMorktLage(pa) {
  try {
    if (pa) {
      document.documentElement.setAttribute('data-tema', 'mork')
      localStorage.setItem('eon_tema', 'mork')
    } else {
      document.documentElement.removeAttribute('data-tema')
      localStorage.removeItem('eon_tema')
    }
  } catch {}
}
if (lasMorktLage()) sattMorktLage(true)

export function AppSchema() {
  const { medarbetare, logOut } = useAuth()
  const [aktiv, setAktiv] = useState('schema')
  const [manad, setManad] = useState(new Date())
  const [pass, setPass] = useState([])
  const [allaPass, setAllaPass] = useState([])
  const [passbytePass, setPassbytePass] = useState([])
  const [dagnoteringar, setDagnoteringar] = useState({}) // alla avdelningars pass för dagvy
  const [allaMedarbetare, setAllaMedarbetare] = useState([])
  const [ansokningar, setAnsokningar] = useState([])
  const [valdDag, setValdDag] = useState(null)
  const [ansokningsDag, setAnsokningsDag] = useState(null) // sparas när man öppnar ansökan
  const [visaAnsokningsModal, setVisaAnsokningsModal] = useState(null)
  const [laddning, setLaddning] = useState(true)
  const [visaHelhetsschema, setVisaHelhetsschema] = useState(false)
  const [visaMinSap, setVisaMinSap] = useState(false)

  useEffect(() => { if (medarbetare) hamtaPass() }, [manad, medarbetare])
  useEffect(() => { if (medarbetare) hamtaDagnoteringar() }, [manad, medarbetare])
  useEffect(() => {
    if (medarbetare) {
      hamtaAnsokningar()
      const interval = setInterval(hamtaAnsokningar, 2000)
      return () => clearInterval(interval)
    }
  }, [medarbetare])
  useEffect(() => { hamtaAllaMedarbetare() }, [])

  async function hamtaAllaMedarbetare() {
    const { data } = await supabase.from('medarbetare').select('id, fornamn, efternamn, avdelning').eq('ar_aktiv', true)
    setAllaMedarbetare(data || [])
  }

  async function hamtaDagnoteringar() {
    const ar = manad.getFullYear()
    const man = manad.getMonth()
    const start = `${ar}-${String(man + 1).padStart(2,'0')}-01`
    const slut = (() => { const d = ar + '-' + String(man+1).padStart(2,'0') + '-' + String(new Date(ar, man+1, 0).getDate()).padStart(2,'0'); return d })()
    const { data } = await supabase
      .from('dagnoteringar')
      .select('datum, text')
      .eq('avdelning', medarbetare.avdelning)
      .gte('datum', start)
      .lte('datum', slut)
    const map = {}
    for (const d of (data || [])) map[d.datum] = d.text
    setDagnoteringar(map)
  }

  async function hamtaPass() {
    setLaddning(true)
    const ar = manad.getFullYear()
    const man = manad.getMonth()
    const start = `${ar}-${String(man + 1).padStart(2,'0')}-01`
    const slutManad = (() => { const d = ar + '-' + String(man+1).padStart(2,'0') + '-' + String(new Date(ar, man+1, 0).getDate()).padStart(2,'0'); return d })()
    const slutBredd = new Date(ar, man + 6, 1).toISOString().split('T')[0]
    const startBredd = new Date(ar, man - 3, 1).toISOString().split('T')[0]

    // Mina pass – 6 månader framåt för passbyte
    const { data: minaPass } = await supabase
      .from('pass')
      .select('*, passtyp:passtyper(*)')
      .eq('medarbetare_id', medarbetare.id)
      .gte('datum', start)
      .lte('datum', slutBredd)

    // Dagvy: alla pass för aktuell månad
    const allaPassData = await hamtaAllaSidor(() => supabase
      .from('pass')
      .select('*, medarbetare:medarbetare(id, fornamn, efternamn, avdelning), passtyp:passtyper(*)')
      .gte('datum', start)
      .lte('datum', slutManad)
    )

    // Passbyte: alla pass 6 månader framåt - hämta i batchar för att undvika 1000-rads gräns
    let passbyteData = []
    let pbFrom = 0
    while (true) {
      const { data: batch } = await supabase
        .from('pass')
        .select('id, datum, medarbetare_id, passtyp_namn, kategori, arbetstimmar, passtyp:passtyper(starttid, sluttid)')
        .gte('datum', startBredd)
        .lte('datum', slutBredd)
        .order('id', { ascending: true })
        .range(pbFrom, pbFrom + 999)
      if (!batch || batch.length === 0) break
      passbyteData = [...passbyteData, ...batch]
      if (batch.length < 1000) break
      pbFrom += 1000
    }

    setPass(minaPass || [])
    setAllaPass(allaPassData || [])
    setPassbytePass(passbyteData || [])
    setLaddning(false)
  }

  async function hamtaAnsokningar() {
    // Hämta egna ansökningar (exkludera passändring-notiser som är riktade till mig via skapad_av)
    const { data: egna } = await supabase
      .from('ansokningar')
      .select('*')
      .eq('skapad_av', medarbetare.id)
      .neq('typ', 'passandring_notis')
      .order('skapad_at', { ascending: false })

    // Hämta passändring-notiser riktade till mig
    const { data: notiser } = await supabase
      .from('ansokningar')
      .select('*')
      .eq('mottagare_id', medarbetare.id)
      .eq('typ', 'passandring_notis')
      .order('skapad_at', { ascending: false })

    const alla = [...(egna || []), ...(notiser || [])]
      .sort((a, b) => new Date(b.skapad_at) - new Date(a.skapad_at))
    setAnsokningar(alla)

    // Uppdatera app-badge med antal olästa
    const olastaCount = alla.filter(arOlast).length
    if ('setAppBadge' in navigator) {
      if (olastaCount > 0) {
        navigator.setAppBadge(olastaCount).catch(() => {})
      } else {
        navigator.clearAppBadge().catch(() => {})
      }
    }
  }

  function getPassForDag(datum) {
    return pass.find(p => p.datum === datum)
  }

  function getAllaPassForDag(datum) {
    return allaPass.filter(p => p.datum === datum)
  }

  const arManad = manad.getFullYear()
  const manadsNr = manad.getMonth()
  let startDow = new Date(arManad, manadsNr, 1).getDay()
  startDow = startDow === 0 ? 6 : startDow - 1
  const antalDagar = new Date(arManad, manadsNr + 1, 0).getDate()
  const manadNamn = manad.toLocaleDateString('sv-SE', { month: 'long', year: 'numeric' })

  return (
    <div className="app-shell">
      <div className="app-sticky-top">
      <div className="app-header">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <EonLogo size={24} borderRadius={5} />
            <span style={{ fontWeight: 700, fontSize: '.92rem' }}>Hej {medarbetare?.fornamn} 👋</span>
          </div>
          {aktiv === 'schema' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                onClick={() => setVisaHelhetsschema(true)}
                className="no-print"
                title="Se hela avdelningens schema"
                style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 6, color: '#fff', padding: '4px 8px', cursor: 'pointer', fontSize: '.85rem' }}
              >👥</button>
              <button
                onClick={() => setVisaMinSap(true)}
                className="no-print"
                title="Mina SAP-registreringar"
                style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 6, color: '#fff', padding: '4px 8px', cursor: 'pointer', fontSize: '.7rem', fontWeight: 700 }}
              >SAP</button>
              <button
                onClick={() => window.print()}
                className="no-print"
                style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 6, color: '#fff', padding: '4px 8px', cursor: 'pointer', fontSize: '.85rem' }}
              >🖨</button>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'rgba(0,0,0,.18)', borderRadius: 20, padding: '3px 10px' }}>
                <button style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '1rem', lineHeight: 1 }}
                  onClick={() => setManad(new Date(arManad, manadsNr - 1, 1))}>‹</button>
                <ManadVal manad={manad} onVal={setManad} stil={{ fontSize: '.74rem', fontWeight: 600 }} />
                <button style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '1rem', lineHeight: 1 }}
                  onClick={() => setManad(new Date(arManad, manadsNr + 1, 1))}>›</button>
              </div>
            </div>
          )}
        </div>
        <div style={{ fontSize: '.7rem', opacity: .75 }}>{medarbetare?.avdelning} · {medarbetare?.schema_typ === '34.5h' ? '34,5 h/v' : '40 h/v'}</div>
      </div>

      <nav className="app-top-nav">
        {[
          { id: 'schema', icon: '📅', label: 'Schema' },
          { id: 'inkorg', icon: '📥', label: 'Inkorg' },
          { id: 'profil', icon: '👤', label: 'Profil' },
        ].map(n => (
          <button key={n.id} className={`app-nav-btn${aktiv === n.id ? ' active' : ''}`} onClick={() => setAktiv(n.id)}>
            <span className="icon">{n.icon}</span>
            {n.label}
            {n.id === 'inkorg' && ansokningar.filter(arOlast).length > 0 && (
              <span style={{ background: '#F11C0A', color: '#fff', borderRadius: 10, padding: '0 4px', fontSize: '.65rem', marginLeft: 2 }}>
                {ansokningar.filter(arOlast).length}
              </span>
            )}
          </button>
        ))}
      </nav>
      </div>

      <div className="app-content">
        {aktiv === 'schema' && (
          <SchemaVy
            dagar={antalDagar}
            startDow={startDow}
            arManad={arManad}
            manadsNr={manadsNr}
            getPassForDag={getPassForDag}
            onDagKlick={(datum) => setValdDag(datum)}
            dagnoteringar={dagnoteringar}
            onAnsok={(typ) => setVisaAnsokningsModal(typ)}
            laddning={laddning}
          />
        )}
        {aktiv === 'inkorg' && <InkorgVy ansokningar={ansokningar} onLast={hamtaAnsokningar} onVisaDag={(datum) => {
          const d = String(datum).substring(0, 10)
          const [y, m] = d.split('-').map(Number)
          setManad(new Date(y, m - 1, 1))
          setAktiv('schema')
          setValdDag(d)
        }} />}
        {aktiv === 'profil' && <ProfilVy medarbetare={medarbetare} onLogout={logOut} />}
      </div>


      {valdDag && (
        <DagModal
          datum={valdDag}
          mittPass={getPassForDag(valdDag)}
          allaPassDagen={getAllaPassForDag(valdDag)}
          allaMedarbetare={allaMedarbetare}
          onClose={() => setValdDag(null)}
          onAnsok={(typ) => { setAnsokningsDag(valdDag); setValdDag(null); setVisaAnsokningsModal(typ) }}
          dagnotis={dagnoteringar[valdDag] || null}
        />
      )}

      {visaAnsokningsModal && (
        <AnsokningsModal
          typ={visaAnsokningsModal}
          medarbetareId={medarbetare?.id}
          avdelning={medarbetare?.avdelning}
          skapadAvNamn={`${medarbetare?.fornamn} ${medarbetare?.efternamn}`}
          mittPass={getPassForDag(ansokningsDag || idagLokal())}
          valdDag={ansokningsDag}
          allaMedarbetare={allaMedarbetare}
          allaPass={passbytePass}
          pass={pass}
          onSend={() => { setVisaAnsokningsModal(null); setAnsokningsDag(null); hamtaAnsokningar() }}
          onClose={() => { setVisaAnsokningsModal(null); setAnsokningsDag(null) }}
        />
      )}

      {visaHelhetsschema && (
        <HelhetsschemaVy
          allaMedarbetare={allaMedarbetare}
          egenAvdelning={medarbetare?.avdelning}
          onClose={() => setVisaHelhetsschema(false)}
        />
      )}

      {visaMinSap && (
        <MinSapVy
          medarbetareId={medarbetare?.id}
          visadManad={manad}
          onClose={() => setVisaMinSap(false)}
        />
      )}
    </div>
  )
}


// Scrolla tabellen i sidled så att aktuell vecka (måndagen) hamnar först efter namnkolumnen.
// attr = data-attribut på rubrikcellerna, nyckel = funktion som ger attributvärde för ett datum.
function scrollaTillAktuellVecka(scroller, attr, varde, stickySelector) {
  if (!scroller) return
  const nu = new Date()
  const dow = (nu.getDay() + 6) % 7
  const mandag = new Date(nu.getFullYear(), nu.getMonth(), nu.getDate() - dow)
  const kandidater = [varde(nu)] // börja på dagens datum
  let th = null
  for (const k of kandidater) {
    th = scroller.querySelector(`th[${attr}="${k}"]`)
    if (th) break
  }
  if (!th) return
  const sticky = scroller.querySelector(stickySelector)
  const vanster = th.getBoundingClientRect().left - scroller.getBoundingClientRect().left + scroller.scrollLeft
  scroller.scrollLeft = Math.max(0, vanster - (sticky ? sticky.offsetWidth : 0) - 2)
}

function HelhetsschemaVy({ allaMedarbetare, egenAvdelning, onClose }) {
  const [manad, setManad] = React.useState(new Date())
  const [fressPass, setFressPass] = React.useState([])
  const [hamtar, setHamtar] = React.useState(true)
  const [historikPerPass, setHistorikPerPass] = React.useState({}) // { pass_id: [loggrader] }
  const [dagnotisPerDatum, setDagnotisPerDatum] = React.useState({}) // { 'YYYY-MM-DD': text }
  const [exporterar, setExporterar] = React.useState(false)
  const [loggRuta, setLoggRuta] = React.useState(null) // { titel, rader } - ändringslogg som visas vid tryck på en ruta
  // Byter man månad ska tabellen hoppa tillbaka till första dagen (vänster och överst)
  const scrollRef = React.useRef(null)
  React.useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTo({ left: 0, top: 0 })
  }, [manad])
  // Aktuell månad: börja på dagens datum. Görs först när passen är hämtade, annars
  // ändras kolumnbredderna när passnamnen fylls i och man hamnar en vecka för tidigt.
  React.useEffect(() => {
    const nu = new Date()
    if (hamtar || manad.getFullYear() !== nu.getFullYear() || manad.getMonth() !== nu.getMonth()) return
    const t = setTimeout(() => scrollaTillAktuellVecka(scrollRef.current, 'data-dag', d => d.getMonth() === manad.getMonth() ? d.getDate() : '', 'th.hs-namnkol'), 60)
    return () => clearTimeout(t)
  }, [manad, hamtar])
  const ar = manad.getFullYear()
  const man = manad.getMonth()
  const antalDagar = new Date(ar, man + 1, 0).getDate()
  const dagar = Array.from({ length: antalDagar }, (_, i) => i + 1)
  const manadNamn = manad.toLocaleDateString('sv-SE', { month: 'long', year: 'numeric' })

  React.useEffect(() => {
    let avbruten = false
    async function hamtaFarskData() {
      setHamtar(true)
      const start = `${ar}-${String(man + 1).padStart(2, '0')}-01`
      const slut = (() => { const d = ar + '-' + String(man+1).padStart(2,'0') + '-' + String(new Date(ar, man+1, 0).getDate()).padStart(2,'0'); return d })()
      const data = await hamtaAllaSidor(() => supabase
        .from('pass')
        .select('id, datum, medarbetare_id, passtyp_namn, kategori, har_atf, atf_fran, atf_till, har_beredskap, har_boka_dag, notering, visa_notering_i_schema, medarbetare:medarbetare(avdelning)')
        .gte('datum', start)
        .lte('datum', slut)
      )
      if (!avbruten) {
        const egnaPass = (data || []).filter(p => p.medarbetare?.avdelning === egenAvdelning)
        setFressPass(egnaPass)
        setHamtar(false)
        // Ändringslogg och dagsnoteringar hämtas efteråt så schemat visas direkt
        try {
          const ids = egnaPass.map(p => p.id)
          const hist = {}
          for (let i = 0; i < ids.length; i += 100) {
            const { data: rader } = await supabase.from('pass_historik')
              .select('pass_id, andrad_at, andrad_av_namn, gammal_passtyp, ny_passtyp, notering')
              .in('pass_id', ids.slice(i, i + 100)).order('andrad_at', { ascending: true })
            for (const r of rader || []) {
              if (!hist[r.pass_id]) hist[r.pass_id] = []
              hist[r.pass_id].push(r)
            }
            if (avbruten) return
          }
          const { data: dn } = await supabase.from('dagnoteringar').select('datum, text')
            .eq('avdelning', egenAvdelning).gte('datum', start).lte('datum', slut)
          const dnMap = {}
          for (const r of dn || []) if (r.text) dnMap[r.datum] = r.text
          if (!avbruten) { setHistorikPerPass(hist); setDagnotisPerDatum(dnMap) }
        } catch (e) { console.error('Kunde inte hämta ändringslogg/dagsnoteringar:', e) }
      }
    }
    hamtaFarskData()
    return () => { avbruten = true }
  }, [ar, man, egenAvdelning])

  const teamet = allaMedarbetare
    .filter(m => m.avdelning === egenAvdelning)
    .sort((a, b) => a.fornamn.localeCompare(b.fornamn, 'sv'))

  function passForCell(medId, dag) {
    const datumStr = `${ar}-${String(man + 1).padStart(2, '0')}-${String(dag).padStart(2, '0')}`
    return fressPass.find(p => p.medarbetare_id === medId && p.datum === datumStr)
  }

  // Veckodag, vecka och röda dagar (söndagar + helgdagar) för kolumnrubriker
  const hsHelgdagar = hamtaHelgdagar(ar)
  function dagInfo(dag) {
    const dow = new Date(ar, man, dag).getDay()
    const datum = `${ar}-${String(man + 1).padStart(2, '0')}-${String(dag).padStart(2, '0')}`
    const helgdag = hsHelgdagar[datum]
    return {
      helgdag,
      rod: dow === 0 || !!helgdag,
      helg: dow === 0 || dow === 6 || !!helgdag,
      bokstav: ['Sön', 'Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör'][dow],
      vecka: (dow === 1 || dag === 1) ? isoVeckaNr(ar, man, dag) : null
    }
  }
  function hsRubrik(dag) {
    const info = dagInfo(dag)
    return (
      <th key={dag} data-dag={dag} className={info.helg ? 'hs-helg' : ''} title={info.helgdag || undefined}
          style={info.rod ? { color: '#c62828', background: '#ffe3e3' } : undefined}>
        <div style={{ fontSize: '.5rem', fontWeight: 700, opacity: .75, lineHeight: 1, height: 8 }}>{info.vecka ? `v.${info.vecka}` : ''}</div>
        <div style={{ fontSize: '.55rem', fontWeight: 600, lineHeight: 1.1 }}>{info.bokstav}</div>
        <div>{dag}</div>
      </th>
    )
  }
  // Röda dagar och helger får grå ruta, men passets egen färg går först
  function hsCellStil(p, dag) {
    const farg = p ? KATEGORI_FARGER[p.kategori] : null
    const info = dagInfo(dag)
    const eget = farg && farg.bg !== 'transparent'
    const bg = eget ? farg.bg : (info.helg ? '#e6e6e6' : undefined)
    if (farg) return { background: bg, color: farg.text }
    return info.helg ? { background: '#e6e6e6' } : {}
  }

  // Ändringslogg som text (för tooltip och Excel-anteckning)
  function loggText(p) {
    const lista = p ? (historikPerPass[p.id] || []) : []
    return lista.map(h =>
      `${new Date(h.andrad_at).toLocaleString('sv-SE')} (${h.andrad_av_namn}): ` +
      `${h.gammal_passtyp !== h.ny_passtyp ? `${h.gammal_passtyp} → ${h.ny_passtyp}` : h.ny_passtyp}` +
      `${h.notering ? ' – ' + h.notering : ''}`
    ).join('\n')
  }

  async function laddaNerExcel() {
    setExporterar(true)
    try {
      const ExcelJS = (await import('exceljs')).default
      const argb = hex => {
        if (!hex || hex === 'transparent') return null
        let h = hex.replace('#', '').toUpperCase()
        if (h.length === 3) h = h.split('').map(ch => ch + ch).join('')
        return /^[0-9A-F]{6}$/.test(h) ? 'FF' + h : null
      }
      // Rensa bort tecken som Excel inte tillåter i texter (annars kommer "reparera filen"-rutan)
      const rent = t => String(t ?? '')
        .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\uFFFE\uFFFF]/g, '')
        .replace(/[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g, '')
      // Rik text: inga tomma delar, alla delar får typsnitt
      const rik = delar => {
        const ok = delar.map(d => ({ ...d, text: rent(d.text), font: { name: 'Calibri', family: 2, ...d.font } })).filter(d => d.text !== '')
        return ok.length ? { richText: ok } : ''
      }
      const wb = new ExcelJS.Workbook()
      const ws = wb.addWorksheet(`${egenAvdelning}`.substring(0, 31))
      const harNoteringar = dagar.some(d => dagnotisPerDatum[`${ar}-${String(man + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`])
      const datumStr = d => `${ar}-${String(man + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
      ws.getColumn(1).width = 24
      dagar.forEach((_, i) => { ws.getColumn(i + 2).width = 9 })
      const tunn = { style: 'thin', color: { argb: 'FFDDDDDD' } }

      // Månadsrubrik överst
      const rubrikText = rent(`${manadNamn.charAt(0).toUpperCase()}${manadNamn.slice(1)} – ${egenAvdelning}`)
      const titel = ws.addRow([rubrikText])
      titel.height = 26
      ws.mergeCells(1, 1, 1, dagar.length + 1)
      titel.getCell(1).font = { bold: true, size: 16, name: 'Calibri' }
      titel.getCell(1).alignment = { horizontal: 'left', vertical: 'middle' }

      // Rad med dagsnoteringar (gäller hela dagen)
      if (harNoteringar) {
        const r = ws.addRow(['Notering', ...dagar.map(d => rent(dagnotisPerDatum[datumStr(d)] || ''))])
        const langsta = Math.max(...dagar.map(d => (dagnotisPerDatum[datumStr(d)] || '').length))
        r.height = Math.min(150, Math.max(22, Math.ceil(langsta / 8) * 12 + 6))
        r.eachCell({ includeEmpty: true }, (c, nr) => {
          c.alignment = { horizontal: nr === 1 ? 'left' : 'center', vertical: 'top', wrapText: true }
          c.font = { bold: nr === 1, size: 8, color: { argb: nr === 1 ? 'FF444444' : 'FF2E7D32' } }
          if (nr > 1 && c.value) c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8F5E9' } }
        })
      }

      // Rubrikrad
      const rubrik = ws.addRow(['Namn', ...dagar.map(() => '')])
      const harHelgnamn = dagar.some(d => dagInfo(d).helgdag)
      rubrik.height = harHelgnamn ? 52 : 34
      rubrik.eachCell({ includeEmpty: true }, (c, nr) => {
        const info = nr > 1 ? dagInfo(dagar[nr - 2]) : null
        const rod = !!info?.rod
        const txt = rod ? 'FFC62828' : 'FFFFFFFF'
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: rod ? 'FFFFE3E3' : 'FFF11C0A' } }
        c.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }
        c.border = { bottom: tunn }
        if (nr === 1) { c.value = 'Namn'; c.font = { bold: true, color: { argb: 'FFFFFFFF' } }; return }
        const delar = []
        if (info.vecka) delar.push({ text: `v.${info.vecka}\n`, font: { size: 7, bold: true, color: { argb: txt } } })
        delar.push({ text: `${info.bokstav} ${dagar[nr - 2]}`, font: { size: 10, bold: true, color: { argb: txt } } })
        if (info.helgdag) delar.push({ text: `\n${info.helgdag}`, font: { size: 7, color: { argb: txt } } })
        c.value = rik(delar)
      })

      // Medarbetare
      teamet.forEach(med => {
        const rad = ws.addRow([rent(`${med.fornamn} ${med.efternamn}`), ...dagar.map(() => '')])
        rad.getCell(1).font = { bold: true }
        rad.getCell(1).alignment = { vertical: 'middle' }
        let hogRad = false
        dagar.forEach((d, i) => {
          const p = passForCell(med.id, d)
          const info = dagInfo(d)
          const c = rad.getCell(i + 2)
          c.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }
          c.border = { top: tunn, bottom: tunn, left: tunn, right: tunn }
          const farg = p ? KATEGORI_FARGER[p.kategori] : null
          const eget = farg && farg.bg !== 'transparent'
          const bg = eget ? argb(farg.bg) : (info.helg ? 'FFE6E6E6' : null)
          if (bg) c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } }
          if (!p) return
          const textFarg = argb(farg?.text) || 'FF444444'
          const delar = [{ text: p.passtyp_namn || '', font: { size: 11, bold: true, color: { argb: textFarg } } }]
          if (p.kategori === 'beviljad_semester') delar.push({ text: ' ✦', font: { size: 11, bold: true, color: { argb: 'FF2E7D32' } } })
          const extra = []
          if (p.har_atf) extra.push(`ATF ${p.atf_fran?.substring(0, 5) || ''}-${p.atf_till?.substring(0, 5) || ''}`)
          else if (p.kategori === 'permission') extra.push('Perm')
          else if (p.kategori === 'flex') extra.push('FLEX')
          if (p.visa_notering_i_schema && p.notering) extra.push(p.notering)
          if (extra.length) {
            hogRad = true
            delar.push({ text: '\n' + extra.join('\n'), font: { size: 7, bold: true, color: { argb: textFarg } } })
          }
          c.value = rik(delar)
          // Beredskap = röd linje, boka dag = blå linje (som i appen)
          if (p.har_beredskap || p.har_boka_dag) {
            c.border = { ...c.border, bottom: { style: 'thick', color: { argb: p.har_beredskap ? 'FFE74C3C' : 'FF3498DB' } } }
          }
          const logg = loggText(p)
          if (logg) c.note = { texts: [{ text: rent(logg).substring(0, 30000), font: { size: 9 } }] }
        })
        rad.height = hogRad ? 34 : 20
      })

      ws.views = [{ state: 'frozen', xSplit: 1, ySplit: harNoteringar ? 3 : 2 }]
      const buffer = await wb.xlsx.writeBuffer()
      const blob = new Blob([buffer], { type: 'application/octet-stream' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `Helhetsschema_${egenAvdelning}_${ar}-${String(man + 1).padStart(2, '0')}.xlsx`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (e) {
      console.error('Excel-export:', e)
    } finally {
      setExporterar(false)
    }
  }

  return (
    <div className="modal-overlay helhetsschema-overlay">
      <div className="modal-box helhetsschema-box">
        {/* Rubrik, stäng-kryss och månadsval ligger kvar överst när man scrollar */}
        <div className="no-print" style={{ position: 'sticky', top: 0, zIndex: 5, background: '#fff' }}>
        <div className="modal-header no-print">
          <h3>Helhetsschema – {egenAvdelning}{hamtar && <span style={{ fontSize: '.68rem', color: '#999', fontWeight: 400, marginLeft: 10 }}>(hämtar senaste...)</span>}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="no-print" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '8px 16px', flexWrap: 'wrap' }}>
          <button className="btn btn-secondary btn-sm" style={{ fontSize: '1.4rem', lineHeight: 1, padding: '4px 16px' }} onClick={() => setManad(new Date(ar, man - 1, 1))}>‹</button>
          <ManadVal manad={manad} onVal={setManad} stil={{ fontWeight: 700, fontSize: '.9rem' }} />
          <button className="btn btn-secondary btn-sm" style={{ fontSize: '1.4rem', lineHeight: 1, padding: '4px 16px' }} onClick={() => setManad(new Date(ar, man + 1, 1))}>›</button>
          <button className="btn btn-primary btn-sm" style={{ marginLeft: 14 }} onClick={laddaNerExcel} disabled={exporterar || hamtar}>{exporterar ? 'Skapar fil...' : 'Ladda ner Excel'}</button>
        </div>
        </div>

        <h2 className="print-only-title" style={{ display: 'none' }}>Helhetsschema – {egenAvdelning} – {manadNamn}</h2>

        <div className="helhetsschema-scroll no-print" ref={scrollRef}>
          <table className="helhetsschema-tabell">
            <thead>
              <tr>
                <th className="hs-namnkol">Namn</th>
                {dagar.map(dag => hsRubrik(dag))}
              </tr>
            </thead>
            <tbody>
              {Object.keys(dagnotisPerDatum).length > 0 && (
                <tr>
                  <td className="hs-namnkol" style={{ fontSize: '.62rem' }}>Notering</td>
                  {dagar.map(dag => {
                    const t = dagnotisPerDatum[`${ar}-${String(man + 1).padStart(2, '0')}-${String(dag).padStart(2, '0')}`]
                    return (
                      <td key={dag} title={t || undefined} style={t ? { background: '#e8f5e9', verticalAlign: 'top', padding: '4px 5px' } : undefined}>
                        {t && <div style={{ fontSize: '.6rem', fontWeight: 700, color: '#2e7d32', lineHeight: 1.25, minWidth: 70, maxWidth: 110, whiteSpace: 'normal', overflowWrap: 'anywhere', textAlign: 'left' }}>{t}</div>}
                      </td>
                    )
                  })}
                </tr>
              )}
              {teamet.map(med => (
                <tr key={med.id}>
                  <td className="hs-namnkol">{med.fornamn} {med.efternamn}</td>
                  {dagar.map(dag => {
                    const p = passForCell(med.id, dag)
                    const farg = p ? KATEGORI_FARGER[p.kategori] : null
                    return (
                      <td key={dag} style={{ ...hsCellStil(p, dag), position: 'relative', cursor: historikPerPass[p?.id]?.length ? 'pointer' : undefined }} title={loggText(p) || undefined}
                          onClick={() => {
                            const rader = p ? historikPerPass[p.id] : null
                            if (rader?.length) setLoggRuta({ titel: `${med.fornamn} ${med.efternamn} · ${new Date(ar, man, dag).toLocaleDateString('sv-SE', { weekday: 'short', day: 'numeric', month: 'short' })}`, rader })
                          }}>
                        {p && historikPerPass[p.id]?.length > 0 && (
                          <span style={{ position: 'absolute', top: 0, right: 0, width: 0, height: 0, borderTop: '7px solid #e74c3c', borderLeft: '7px solid transparent' }} />
                        )}
                        {p?.passtyp_namn || ''}
                        {p?.kategori === 'beviljad_semester' && <span style={{ color: '#2e7d32', fontWeight: 700 }}> ✦</span>}
                        {p && (p.har_atf || p.kategori === 'permission' || p.kategori === 'flex') && (
                          <div style={{ fontSize: '.55rem', fontWeight: 700, lineHeight: 1, opacity: .9 }}>
                            {p.har_atf
                              ? `ATF ${p.atf_fran?.substring(0,5) || ''}-${p.atf_till?.substring(0,5) || ''}`
                              : p.kategori === 'permission' ? 'Perm' : 'FLEX'}
                          </div>
                        )}
                        {p && (p.har_beredskap || p.har_boka_dag) && (
                          <div style={{ marginTop: 2, height: 4, borderTop: `3px solid ${p.har_beredskap ? '#e74c3c' : '#3498db'}` }} title={p.har_beredskap ? 'Beredskap' : 'Bokad dag'} />
                        )}
                        {p && p.visa_notering_i_schema && p.notering && (
                          <div style={{ fontSize: '.5rem', fontWeight: 700, lineHeight: 1.1, opacity: .9, maxWidth: 60, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.notering}>
                            {p.notering}
                          </div>
                        )}
                      </td>
                    )
                  })}
                </tr>
              ))}
              {teamet.length === 0 && (
                <tr><td colSpan={dagar.length + 1} style={{ textAlign: 'center', color: '#999', padding: 20 }}>Inga medarbetare hittades.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {loggRuta && (
          <div className="modal-overlay no-print" style={{ zIndex: 10002 }} onClick={e => e.target === e.currentTarget && setLoggRuta(null)}>
            <div className="modal-box" style={{ maxWidth: 340 }}>
              <div className="modal-header">
                <h3>{loggRuta.titel}</h3>
                <button className="modal-close" onClick={() => setLoggRuta(null)}>×</button>
              </div>
              <div className="modal-body">
                <div style={{ fontSize: '.7rem', fontWeight: 700, color: '#bbb', marginBottom: 6, letterSpacing: '.5px' }}>ÄNDRINGSLOGG</div>
                {[...loggRuta.rader].reverse().map((h, i) => (
                  <div key={i} style={{ fontSize: '.78rem', padding: '6px 8px', background: '#f9f9f9', borderRadius: 4, marginBottom: 5 }}>
                    <div style={{ fontWeight: 600 }}>
                      {h.gammal_passtyp !== h.ny_passtyp ? `${h.gammal_passtyp} → ${h.ny_passtyp}` : h.ny_passtyp}
                      {h.notering ? ` · ${h.notering}` : ''}
                    </div>
                    <div style={{ color: '#aaa', fontSize: '.68rem' }}>{h.andrad_av_namn} · {new Date(h.andrad_at).toLocaleString('sv-SE')}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Utskriftsversion: samma data, uppdelad i block om 15 dagar så
            inget klipps bort i sidled - visas ALDRIG på skärmen. */}
        <div className="print-only" style={{ display: 'none' }}>
          {(() => {
            const DAGAR_PER_BLOCK = 15
            const block = []
            for (let i = 0; i < dagar.length; i += DAGAR_PER_BLOCK) {
              block.push(dagar.slice(i, i + DAGAR_PER_BLOCK))
            }
            return block.map((dagBlock, bi) => (
              <table key={bi} className="helhetsschema-tabell print-block" style={{ pageBreakAfter: bi < block.length - 1 ? 'always' : 'auto', width: '100%', marginBottom: 16 }}>
                <thead>
                  <tr>
                    <th className="hs-namnkol">Namn</th>
                    {dagBlock.map(dag => hsRubrik(dag))}
                  </tr>
                </thead>
                <tbody>
                  {teamet.map(med => (
                    <tr key={med.id}>
                      <td className="hs-namnkol">{med.fornamn} {med.efternamn}</td>
                      {dagBlock.map(dag => {
                        const p = passForCell(med.id, dag)
                        const farg = p ? KATEGORI_FARGER[p.kategori] : null
                        return (
                          <td key={dag} style={hsCellStil(p, dag)}>
                            {p?.passtyp_namn || ''}
                            {p && (p.har_atf || p.kategori === 'permission' || p.kategori === 'flex') && (
                              <div style={{ fontSize: '.55rem', fontWeight: 700, lineHeight: 1, opacity: .9 }}>
                                {p.har_atf
                                  ? `ATF ${p.atf_fran?.substring(0,5) || ''}-${p.atf_till?.substring(0,5) || ''}`
                                  : p.kategori === 'permission' ? 'Perm' : 'FLEX'}
                              </div>
                            )}
                            {p && (p.har_beredskap || p.har_boka_dag) && (
                              <div style={{ marginTop: 2, height: 4, borderTop: `3px solid ${p.har_beredskap ? '#e74c3c' : '#3498db'}` }} />
                            )}
                            {p && p.visa_notering_i_schema && p.notering && (
                              <div style={{ fontSize: '.5rem', fontWeight: 700, lineHeight: 1.1, opacity: .9, maxWidth: 60, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {p.notering}
                              </div>
                            )}
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            ))
          })()}
        </div>
      </div>
    </div>
  )
}

function MinSapVy({ medarbetareId, visadManad, onClose }) {
  const [manad, setManad] = React.useState(new Date(visadManad))
  const [laddar, setLaddar] = React.useState(true)
  const [rader, setRader] = React.useState([])
  const manadNamn = manad.toLocaleDateString('sv-SE', { month: 'long', year: 'numeric' })

  React.useEffect(() => {
    if (!medarbetareId) return
    let avbruten = false
    async function hamta() {
      setLaddar(true)
      const ar = manad.getFullYear(), man = manad.getMonth()
      const start = `${ar}-${String(man + 1).padStart(2, '0')}-01`
      const slut = (() => { const d = ar + '-' + String(man+1).padStart(2,'0') + '-' + String(new Date(ar, man+1, 0).getDate()).padStart(2,'0'); return d })()

      // Alla egna pass med en passändring den här månaden, som faktiskt kan ge ersättning
      const { data: passData } = await supabase
        .from('pass')
        .select('id, datum, passtyp_namn, kategori, har_passandring')
        .eq('medarbetare_id', medarbetareId)
        .eq('har_passandring', true)
        .not('kategori', 'in', '(passbyte,overtid,semester,beviljad_semester,foraldraledighet,beviljad_foraldraledighet,permission,flex,sjukdom)')
        .gte('datum', start)
        .lte('datum', slut)
        .order('datum')

      if (!passData || passData.length === 0) {
        if (!avbruten) { setRader([]); setLaddar(false) }
        return
      }

      const { data: historikData } = await supabase
        .from('pass_historik')
        .select('pass_id, andrad_at, gammal_passtyp, ny_passtyp, gammal_kategori, ny_kategori')
        .in('pass_id', passData.map(p => p.id))
        .order('andrad_at', { ascending: false })

      const { data: passtyperData } = await supabase.from('passtyper').select('namn, starttid, sluttid')
      const passtyperMap = {}
      for (const pt of passtyperData || []) passtyperMap[pt.namn.toUpperCase()] = pt

      const resultat = []
      for (const p of passData) {
        if (p.kategori === 'ledig') continue
        const historikForPass = (historikData || []).filter(h => h.pass_id === p.id)
        const riktigaAndringar = historikForPass.filter(h => h.gammal_passtyp !== h.ny_passtyp || h.gammal_kategori !== h.ny_kategori)
        if (riktigaAndringar.length === 0) continue
        const senaste = riktigaAndringar[0]
        const ersattning = beraknaSkiftersattning(senaste.andrad_at, p.datum)
        // Bara med om det faktiskt ger ersättning - inte "Ingen ersättning"
        if (!ersattning || !ersattning.divisor) continue

        const gammalTider = senaste.gammal_passtyp ? passtyperMap[basPassNamn(senaste.gammal_passtyp)] : null
        const nyaTider = senaste.ny_passtyp ? passtyperMap[basPassNamn(senaste.ny_passtyp)] : null
        const timmarAttRegistrera = (gammalTider && nyaTider)
          ? beraknaRegistreradeTimmar(gammalTider.starttid, gammalTider.sluttid, nyaTider.starttid, nyaTider.sluttid)
          : null

        // Inga timmar utanför det ursprungliga passets tid (t.ex. samma tider) = ingen ersättning
        if (timmarAttRegistrera === 0) continue

        resultat.push({
          datum: p.datum,
          fran: senaste.gammal_passtyp,
          till: senaste.ny_passtyp,
          ersattning,
          timmarAttRegistrera
        })
      }
      if (!avbruten) { setRader(resultat); setLaddar(false) }
    }
    hamta()
    return () => { avbruten = true }
  }, [medarbetareId, manad])

  return (
    <div className="modal-overlay">
      <div className="modal-box" style={{ maxWidth: 420 }}>
        <div className="modal-header">
          <h3>SAP – mina registreringar</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 10 }}>
            <button className="btn btn-secondary btn-sm" style={{ fontSize: '1.4rem', lineHeight: 1, padding: '4px 16px' }} onClick={() => setManad(new Date(manad.getFullYear(), manad.getMonth() - 1, 1))}>‹</button>
            <span style={{ fontWeight: 700, textTransform: 'capitalize', fontSize: '.9rem' }}>{manadNamn}</span>
            <button className="btn btn-secondary btn-sm" style={{ fontSize: '1.4rem', lineHeight: 1, padding: '4px 16px' }} onClick={() => setManad(new Date(manad.getFullYear(), manad.getMonth() + 1, 1))}>›</button>
          </div>
          <div className="info-box info-box-yellow" style={{ fontSize: '.78rem', marginBottom: 10 }}>
            Obs! Dagtidspersonal har inte rätt till skiftersättning utan godkännande från sin chef. Har du inte fått det godkänt, bortse från det som står under SAP.
          </div>
          <p style={{ fontSize: '.78rem', color: '#999', marginTop: 0, textAlign: 'center' }}>
            Dina passändringar den här månaden som ger skiftersättning.
          </p>
          {laddar ? (
            <p style={{ textAlign: 'center', color: '#999' }}>Hämtar...</p>
          ) : rader.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#999' }}>Inga passändringar som ger ersättning denna månad.</p>
          ) : (
            rader.map((r, i) => (
              <div key={i} style={{ padding: '10px 12px', background: '#f9f9f9', borderRadius: 8, border: '1px solid #ececec', marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <strong style={{ fontSize: '.85rem' }}>{new Date(r.datum + 'T12:00:00').toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' })}</strong>
                  <span style={{ fontSize: '.78rem', fontWeight: 700, color: '#c0392b' }}>{r.ersattning.text}</span>
                </div>
                <div style={{ fontSize: '.8rem', color: '#555', marginTop: 2 }}>
                  {r.fran || '–'} → {r.till || '–'}
                  {r.timmarAttRegistrera !== null && r.timmarAttRegistrera > 0 && (
                    <span> · {r.timmarAttRegistrera}h att registrera</span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

function SchemaVy({ dagar, startDow, arManad, manadsNr, getPassForDag, onDagKlick, onAnsok, laddning, dagnoteringar = {} }) {
  const helgdagar = hamtaHelgdagar(arManad)
  return (
    <div>
      {/* KALENDER */}
      <div style={{ display: 'flex', flexWrap: 'wrap', background: '#e8e8e8', borderBottom: '1px solid #e8e8e8' }}>
        {['M','T','O','T','F','L','S'].map((d, i) => (
          <div key={i} style={{ width: 'calc(14.2857% - 1px)', marginRight: 1, marginBottom: 1, background: '#f8f8f8', padding: '6px 2px', textAlign: 'center', fontSize: '.6rem', fontWeight: 700, color: '#bbb', textTransform: 'uppercase' }}>{d}</div>
        ))}
        {Array.from({ length: startDow }).map((_, i) => (
          <div key={`e-${i}`} style={{ width: 'calc(14.2857% - 1px)', marginRight: 1, marginBottom: 1, background: '#fafafa', minHeight: 38 }} />
        ))}
        {Array.from({ length: dagar }).map((_, i) => {
          const dag = i + 1
          const datum = `${arManad}-${String(manadsNr + 1).padStart(2, '0')}-${String(dag).padStart(2, '0')}`
          const p = getPassForDag(datum)
          const erIdag = datum === idagLokal()
          const helgdag = helgdagar[datum]
          const dagObj = new Date(datum + 'T12:00:00')
          const erSondag = dagObj.getDay() === 0
          const passNamn = p?.passtyp_namn || ''
          const kategoriNamn = p?.kategori || ''
          const erSemester = kategoriNamn === 'semester' || kategoriNamn === 'beviljad_semester'
          const erPassandring = p?.kategori === 'passandring' || p?.har_passandring === true
          const erBeviljad = p?.kategori === 'beviljad_semester'
          const erForaldraledig = kategoriNamn === 'foraldraledighet' || kategoriNamn === 'beviljad_foraldraledighet'
          const erBeviljadForaldraledig = p?.kategori === 'beviljad_foraldraledighet'
          const erBokaDag = !!p?.har_boka_dag
          const erPassbyte = p?.kategori === 'passbyte'
          const erSjukdom = p?.kategori === 'sjukdom'
          const erBeredskap = !!p?.har_beredskap
          const erLedig = p?.kategori === 'ledig'
          const erOvertid = p?.kategori === 'overtid'

          // Bakgrundsfärg
          let bg = '#fff'
          if (erIdag) bg = '#c8e6c9'
          else if (erSemester || erBeviljad) bg = '#fffde7'
          else if (erForaldraledig) bg = '#e0f7fa'
          else if (erPassbyte) bg = '#fce4ec'
          else if (erSjukdom) bg = '#ede7f6'
          else if (erLedig) bg = '#eeeeee'
          else if (erOvertid) bg = '#fff3e0'

          let textColor = erIdag ? '#1b5e20' : '#1a1a1a'
          if (erSemester || erBeviljad) textColor = '#827717'
          if (erForaldraledig) textColor = '#00695c'
          if (erPassbyte) textColor = '#ad1457'
          if (erSjukdom) textColor = '#6a1b9a'
          if (erLedig) textColor = '#777'
          if (erOvertid) textColor = '#e65100'

          const harNotis = dagnoteringar[datum]
          return (
            <div
              key={dag}
              onClick={() => onDagKlick(datum)}
              style={{
                width: 'calc(14.2857% - 1px)', marginRight: 1, marginBottom: 1,
                background: bg,
                minHeight: 40, padding: '3px 2px', textAlign: 'center',
                cursor: 'pointer', fontSize: '.72rem',
                color: textColor,
                fontWeight: erIdag ? 700 : 400,
                transition: 'background .12s',
                position: 'relative'
              }}
            >
              {/* Passändring: gul stjärna i höger hörn, försvinner ALDRIG */}
              {erPassandring && !erIdag && (
                <span style={{ position: 'absolute', top: 0, right: 1, fontSize: '.65rem', lineHeight: 1 }}>⭐</span>
              )}
              {/* Beviljad semester: liten grön stjärna i höger hörn */}
              {erBeviljad && !erIdag && (
                <span style={{ position: 'absolute', top: 0, right: erPassandring ? 12 : 1, fontSize: '.5rem', lineHeight: 1, color: '#2e7d32' }}>✦</span>
              )}
              {erBeviljadForaldraledig && !erIdag && (
                <span style={{ position: 'absolute', top: 0, right: erPassandring ? 12 : 1, fontSize: '.5rem', lineHeight: 1, color: '#2e7d32' }}>✦</span>
              )}
              {dagObj.getDay() === 1 && (
                <div style={{ fontSize: '.48rem', color: erIdag ? 'rgba(27,94,32,.6)' : '#bbb', fontWeight: 700, lineHeight: 1, marginBottom: 1 }}>
                  v.{(() => { const d2=new Date(datum+'T12:00:00'); d2.setDate(d2.getDate()+3-(d2.getDay()+6)%7); const w1=new Date(d2.getFullYear(),0,4); return 1+Math.round(((d2-w1)/86400000-3+(w1.getDay()+6)%7)/7) })()}
                </div>
              )}
              <div style={{ fontWeight: erIdag ? 700 : (helgdag || erSondag) ? 700 : 400, marginBottom: 1, color: erIdag ? '#1b5e20' : (helgdag || erSondag) && !p ? '#c62828' : undefined }}>{dag}</div>
              {helgdag && !erIdag && <div style={{ fontSize: '.48rem', color: '#c62828', fontStyle: 'italic', lineHeight: 1.1, marginBottom: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '95%' }}>{helgdag}</div>}
              {p && passNamn && (
                <div style={{
                  fontSize: '.6rem', fontWeight: 700,
                  background: erIdag ? 'rgba(27,94,32,.15)' : 'rgba(0,0,0,.12)',
                  color: erIdag ? '#1b5e20' : textColor,
                  borderRadius: 3, padding: '1px 3px',
                  display: 'inline-block', maxWidth: '92%',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                }}>
                  {passNamn}
                </div>
              )}
              {p && ['X','SX','LX','SJX'].includes(passNamn) && p.arbetstimmar !== null && p.arbetstimmar !== undefined && !erIdag && (
                <div style={{ fontSize: '.5rem', color: textColor, opacity: .7, lineHeight: 1 }}>{p.arbetstimmar}h</div>
              )}
              {p && (p.har_atf || p.kategori === 'permission' || p.kategori === 'flex') && !erIdag && (
                <div style={{ fontSize: '.48rem', fontWeight: 700, color: textColor, opacity: .9, lineHeight: 1 }}>
                  {p.har_atf
                    ? `ATF ${p.atf_fran?.substring(0,5) || ''}-${p.atf_till?.substring(0,5) || ''}`
                    : p.kategori === 'permission' ? 'Perm' : 'FLEX'}
                </div>
              )}
              {p && (p.har_beredskap || p.har_boka_dag) && !erIdag && (
                <div style={{ marginTop: 1, height: 4, borderTop: `3px solid ${p.har_beredskap ? '#e74c3c' : '#3498db'}` }} title={p.har_beredskap ? 'Beredskap' : 'Bokad dag'} />
              )}
              {p && p.visa_notering_i_schema && p.notering && !erIdag && (
                <div style={{ fontSize: '.46rem', fontWeight: 700, color: textColor, opacity: .9, lineHeight: 1.1, maxWidth: '92%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.notering}>
                  {p.notering}
                </div>
              )}
              {harNotis && (
                <div style={{ fontSize: '.48rem', background: '#e8f5e9', color: '#2e7d32', borderRadius: 2, padding: '0 2px', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '95%', lineHeight: 1.3 }}>
                  🗒️
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* KOMMANDE PASS */}
      <div style={{ padding: '8px 13px' }}>
        <div style={{ fontSize: '.72rem', fontWeight: 700, color: '#bbb', marginBottom: 6, letterSpacing: '.5px' }}>KOMMANDE PASS</div>
        {laddning ? <div className="spinner" style={{ margin: '20px auto' }} /> : (
          <KommandePass arManad={arManad} manadsNr={manadsNr} dagar={dagar} getPassForDag={getPassForDag} onKlick={onDagKlick} />
        )}

      </div>
    </div>
  )
}

function KommandePass({ arManad, manadsNr, dagar, getPassForDag, onKlick }) {
  const idag = new Date()
  const kommande = []
  for (let d = 1; d <= dagar && kommande.length < 4; d++) {
    const datum = `${arManad}-${String(manadsNr + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
    const p = getPassForDag(datum)
    if (p && new Date(datum) >= idag) kommande.push({ datum, pass: p })
  }

  if (kommande.length === 0) return <p style={{ color: '#bbb', fontSize: '.8rem', textAlign: 'center', padding: '10px 0' }}>Inga fler pass denna månad</p>

  return kommande.map(({ datum, pass: p }) => {
    const farger = KATEGORI_FARGER[p.kategori]
    const { erSemester } = tolkPassNamn(p.passtyp_namn)
    return (
      <div key={datum} style={{ borderRadius: 6, padding: '7px 12px', marginBottom: 5, cursor: 'pointer', background: farger?.bg, borderLeft: `3px solid ${farger?.text}` }} onClick={() => onKlick(datum)}>
        <div style={{ fontWeight: 700, fontSize: '.88rem', color: farger?.text }}>
          {p.passtyp_namn}
          {erSemester && <span style={{ fontSize: '.72rem', marginLeft: 5, opacity: .8 }}>· Semester</span>}
          {p.passtyp && ` – ${p.passtyp.starttid}–${p.passtyp.sluttid}`}
        </div>
        <div style={{ fontSize: '.75rem', color: farger?.text, marginTop: 2, opacity: .85 }}>
          {p.passtyp?.arbetstimmar ? `${p.passtyp.arbetstimmar}h` : ''}
        </div>
        <div style={{ fontSize: '.7rem', color: farger?.text, marginTop: 3, opacity: .65 }}>
          {new Date(datum).toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'long' })}
        </div>
      </div>
    )
  })
}

function PassHistorik({ passId, kategori, harPassandring, skiftDatum, nyStarttid, nySluttid }) {
  const [historik, setHistorik] = React.useState([])
  const [passtyperMap, setPasstyperMap] = React.useState({})
  React.useEffect(() => {
    if (!passId) return
    supabase.from('pass_historik').select('*').eq('pass_id', passId)
      .order('andrad_at', { ascending: false })
      .then(({ data }) => setHistorik(data || []))
    supabase.from('passtyper').select('namn, starttid, sluttid')
      .then(({ data }) => {
        const map = {}
        for (const pt of data || []) map[pt.namn] = pt
        setPasstyperMap(map)
      })
  }, [passId])
  if (!passId || historik.length === 0) return null

  const visaErsattning = harPassandring && kategori && kategori !== 'passbyte' && kategori !== 'overtid'
  // Hoppa över rena ATF-loggar (samma passtyp/kategori före och efter) – bara
  // riktiga passändringar ska räknas när vi letar efter "senaste ändringen"
  const riktigaAndringar = historik.filter(h => h.gammal_passtyp !== h.ny_passtyp || h.gammal_kategori !== h.ny_kategori)
  const ersattning = (visaErsattning && riktigaAndringar.length > 0) ? beraknaSkiftersattning(riktigaAndringar[0].andrad_at, skiftDatum) : null

  // Senaste RIKTIGA historikposten = det pass man ändrade FRÅN senast
  const ursprungligtPasstyp = riktigaAndringar[0]?.gammal_passtyp
  const ursprungligaTider = ursprungligtPasstyp ? passtyperMap[basPassNamn(ursprungligtPasstyp)] : null
  const registreradeTimmar = (visaErsattning && ursprungligaTider && nyStarttid && nySluttid)
    ? beraknaRegistreradeTimmar(ursprungligaTider.starttid, ursprungligaTider.sluttid, nyStarttid, nySluttid)
    : null

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: '.7rem', fontWeight: 700, color: '#bbb', marginBottom: 5, letterSpacing: '.5px' }}>ÄNDRINGSLOGG</div>
      {ersattning && registreradeTimmar !== 0 && (
        <div style={{ fontSize: '.78rem', fontWeight: 700, padding: '7px 10px', background: '#fff3e0', color: '#e65100', borderRadius: 5, marginBottom: 7 }}>
          💰 Skiftersättning: {ersattning.text}
          {ersattning.divisor !== null && registreradeTimmar !== null && (
            <div style={{ marginTop: 3 }}>⏱ Timmar att registrera: {registreradeTimmar}h</div>
          )}
        </div>
      )}
      {historik.map(h => (
        <div key={h.id} style={{ fontSize: '.75rem', padding: '5px 8px', background: '#f9f9f9', borderRadius: 4, marginBottom: 4 }}>
          <div style={{ fontWeight: 600 }}>
            {h.gammal_passtyp !== h.ny_passtyp ? `${h.gammal_passtyp} → ${h.ny_passtyp}` : h.ny_passtyp}
            {h.notering ? ` · ${h.notering}` : ''}
          </div>
          <div style={{ color: '#aaa', fontSize: '.68rem' }}>
            {h.andrad_av_namn} · {new Date(h.andrad_at).toLocaleString('sv-SE')}
          </div>
        </div>
      ))}
    </div>
  )
}

// Genitiv på svenska: Anna -> Annas, Jonas -> Jonas
const agsNamn = n => (!n ? n : /[sxzSXZ]$/.test(n) ? n : n + 's')

const MANAD_KORT = ['Jan','Feb','Mar','Apr','Maj','Jun','Jul','Aug','Sep','Okt','Nov','Dec']

// Klickbar månadsrubrik som öppnar en månadsväljare (år + 12 månader)
function ManadVal({ manad, onVal, stil }) {
  const [open, setOpen] = useState(false)
  const [valtAr, setValtAr] = useState(manad.getFullYear())
  const namn = manad.toLocaleDateString('sv-SE', { month: 'long', year: 'numeric' })
  const nu = new Date()
  return (
    <>
      <button
        type="button"
        onClick={() => { setValtAr(manad.getFullYear()); setOpen(true) }}
        style={{ background: 'none', border: 'none', color: 'inherit', font: 'inherit', cursor: 'pointer', textTransform: 'capitalize', padding: '2px 4px', ...stil }}
      >{namn} <span style={{ fontSize: '.7em', opacity: .7 }}>▾</span></button>
      {(manad.getFullYear() !== nu.getFullYear() || manad.getMonth() !== nu.getMonth()) && (
        <button
          type="button"
          onClick={() => onVal(new Date(nu.getFullYear(), nu.getMonth(), 1))}
          style={{ background: 'none', border: '1px solid currentColor', borderRadius: 10, color: 'inherit', cursor: 'pointer', fontSize: '.62rem', fontWeight: 700, padding: '1px 7px', lineHeight: 1.4, opacity: .85 }}
        >Idag</button>
      )}
      {open && (
        <div className="modal-overlay no-print" style={{ zIndex: 10001 }} onClick={e => e.target === e.currentTarget && setOpen(false)}>
          <div className="modal-box" style={{ maxWidth: 300, color: '#222' }}>
            <div className="modal-header">
              <button className="btn btn-secondary btn-sm" style={{ fontSize: '1.2rem', lineHeight: 1, padding: '4px 14px' }} onClick={() => setValtAr(valtAr - 1)}>‹</button>
              <h3>{valtAr}</h3>
              <button className="btn btn-secondary btn-sm" style={{ fontSize: '1.2rem', lineHeight: 1, padding: '4px 14px' }} onClick={() => setValtAr(valtAr + 1)}>›</button>
            </div>
            <div className="modal-body" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              {MANAD_KORT.map((m, i) => {
                const valdMan = valtAr === manad.getFullYear() && i === manad.getMonth()
                const dennaMan = valtAr === nu.getFullYear() && i === nu.getMonth()
                return (
                  <button key={m}
                    onClick={() => { onVal(new Date(valtAr, i, 1)); setOpen(false) }}
                    style={{
                      padding: '10px 0', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: '.85rem',
                      border: dennaMan && !valdMan ? '2px solid #F11C0A' : '1px solid #ddd',
                      background: valdMan ? '#F11C0A' : '#fff',
                      color: valdMan ? '#fff' : '#333'
                    }}>{m}</button>
                )
              })}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

function DagModal({ datum, mittPass, allaPassDagen, allaMedarbetare, onClose, onAnsok, dagnotis }) {
  const farger = mittPass ? KATEGORI_FARGER[mittPass.kategori] : null

  // Gruppera pass per avdelning i rätt ordning
  const passPerAvdelning = AVDELNINGAR.reduce((acc, avd) => {
    const medIAvd = allaMedarbetare.filter(m => m.avdelning === avd)
    const passIAvd = allaPassDagen.filter(p => {
      const medId = p.medarbetare_id || p.medarbetare?.id
      if (!medIAvd.some(m => m.id === medId)) return false
      const n = (p.passtyp_namn || '').toUpperCase().trim()
      if (!n) return false
      if (n.startsWith('S')) return false // semester
      if (n.startsWith('L')) return false // ledig
      if (n === 'V') return false // veckovila
      if (['ledig','semester','beviljad_semester','foraldraledighet','beviljad_foraldraledighet'].includes(p.kategori)) return false
      return true
    })
    if (passIAvd.length > 0) {
      // Sortera efter när passet börjar (F, D2, E, N ...)
      acc[avd] = [...passIAvd].sort((a, b) => {
        // D-dagar (icke operativa) näst sist och X-dagar allra sist i gruppen
        const rang = p => { const n = (p.passtyp_namn || '').toUpperCase().trim(); return n === 'X' ? 2 : n === 'D' ? 1 : 0 }
        const aD = rang(a), bD = rang(b)
        if (aD !== bD) return aD - bD
        const ta = a.passtyp?.starttid || '99:99'
        const tb = b.passtyp?.starttid || '99:99'
        if (ta !== tb) return ta < tb ? -1 : 1
        return (a.passtyp_namn || '').localeCompare(b.passtyp_namn || '', 'sv')
      })
    }
    return acc
  }, {})

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth: 340, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div className="modal-header" style={{ flexShrink: 0, background: '#fff' }}>
          <h3>📅 {new Date(datum).toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'long' })}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1, overflow: 'hidden', paddingBottom: 0 }}>
          <div style={{ flexShrink: 0 }}>
          {/* Ansökningsknappar överst */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
            <button className="btn btn-secondary btn-sm" style={{ flex: 1, fontSize: '.75rem' }} onClick={() => { onAnsok('ledighet'); onClose() }}>🏖 Ledighetsansökan</button>
            <button className="btn btn-secondary btn-sm" style={{ flex: 1, fontSize: '.75rem' }} onClick={() => { onAnsok('passbyte'); onClose() }}>🔄 Passbyte</button>
            <button className="btn btn-secondary btn-sm" style={{ flex: 1, fontSize: '.75rem' }} onClick={() => { onAnsok('boka_dag'); onClose() }}>📌 Boka dag</button>
            {mittPass && (
              <button className="btn btn-secondary btn-sm" style={{ flex: 1, fontSize: '.75rem' }} onClick={() => { onAnsok('atf'); onClose() }}>⏱ ATF</button>
            )}
            {mittPass && (mittPass.passtyp_namn || '').toUpperCase().trim() === 'D' && datum >= idagLokal() && (
              <button className="btn btn-secondary btn-sm" style={{ flex: 1, fontSize: '.75rem' }} onClick={() => { onAnsok('flytta_pass'); onClose() }}>📅 Flytta D-dag</button>
            )}
          </div>

          </div>

          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingBottom: 16, borderTop: '1px solid #eee', paddingTop: 10 }}>
          {/* Dagnotering om finns */}
          {dagnotis && (
            <div style={{ background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 6, padding: '8px 12px', marginBottom: 10, fontSize: '.82rem', color: '#2e7d32' }}>
              🗒️ {dagnotis}
            </div>
          )}
          {mittPass?.har_atf && (
            <div style={{ background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: 6, padding: '8px 12px', marginBottom: 10, fontSize: '.82rem', color: '#0d47a1', fontWeight: 700 }}>
              ⏱ ATF registrerat: {mittPass.atf_fran?.substring(0,5)}–{mittPass.atf_till?.substring(0,5)}
            </div>
          )}
          {/* Mitt pass */}
          {mittPass ? (
            <div style={{ background: farger?.bg || '#f5f5f5', borderLeft: `3px solid ${farger?.text || '#aaa'}`, borderRadius: 6, padding: '10px 12px', marginBottom: 10 }}>
              <div style={{ fontSize: '.72rem', color: farger?.text || '#666', opacity: .7, marginBottom: 2 }}>Ditt pass</div>
              <div style={{ fontWeight: 700, color: farger?.text || '#333' }}>
                {mittPass.passtyp_namn}
                {['X','SX','LX','SJX'].includes(mittPass.passtyp_namn) && mittPass.arbetstimmar > 0 && (
                  <span style={{ fontWeight: 400, fontSize: '.8rem', marginLeft: 5 }}>{mittPass.arbetstimmar}h</span>
                )}
                {mittPass.kategori === 'passandring' && <span style={{ marginLeft: 4 }}>⭐</span>}
                {mittPass.kategori === 'beviljad_semester' && <span style={{ marginLeft: 4, color: '#2e7d32' }}>✦</span>}
                {mittPass.kategori === 'beviljad_foraldraledighet' && <span style={{ marginLeft: 4, color: '#2e7d32' }}>✦</span>}
              </div>
              <div style={{ fontSize: '.8rem', color: farger?.text || '#555', opacity: .8, marginTop: 2 }}>
                {mittPass.passtyp?.starttid}–{mittPass.passtyp?.sluttid}
                {mittPass.arbetstimmar > 0 && ` · ${mittPass.arbetstimmar}h`}
              </div>
            </div>
          ) : (
            <div style={{ color: '#bbb', fontSize: '.8rem', marginBottom: 10 }}>Ingen tjänst denna dag</div>
          )}
          {/* Passhistorik */}
          <PassHistorik
            passId={mittPass?.id}
            kategori={mittPass?.kategori}
            harPassandring={mittPass?.har_passandring}
            skiftDatum={datum}
            nyStarttid={mittPass?.passtyp?.starttid}
            nySluttid={mittPass?.passtyp?.sluttid}
          />

          {Object.entries(passPerAvdelning).map(([avd, passLista]) => (
            <div key={avd} style={{ marginBottom: 12 }}>
              <div style={{ fontSize: '.7rem', fontWeight: 700, color: '#1565c0', marginBottom: 5, letterSpacing: '.5px' }}>
                {avd}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {passLista.map(p => (
                  <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#f5f5f5', borderRadius: 5 }}>
                    <span style={{ fontWeight: 800, color: '#F11C0A', width: 28, fontSize: '.8rem', flexShrink: 0 }}>
                      <span style={{ borderBottom: p.har_beredskap ? '3px solid #d32f2f' : p.har_boka_dag ? '3px solid #1976d2' : 'none', paddingBottom: 1 }}>{p.passtyp_namn}</span>
                    </span>
                    <span style={{ fontSize: '.85rem', fontWeight: 600 }}>{p.medarbetare?.fornamn} {p.medarbetare?.efternamn}</span>
                    {p.passtyp?.starttid && <span style={{ marginLeft: 'auto', fontSize: '.72rem', color: '#aaa' }}>{p.passtyp.starttid?.substring(0,5)}–{p.passtyp.sluttid?.substring(0,5)}</span>}
                  </div>
                ))}
              </div>
            </div>
          ))}

          {Object.keys(passPerAvdelning).length === 0 && (
            <p style={{ color: '#bbb', fontSize: '.8rem' }}>Inga kollegor jobbar denna dag</p>
          )}
          </div>
        </div>

      </div>
    </div>
  )
}

function InkorgVy({ ansokningar, onLast, onVisaDag }) {
  const TYPER = { ledighet: 'Ledighetsansökan', foraldraledighet: 'Föräldraledighet', passbyte: 'Passbyte', boka_dag: 'Boka dag', atf: 'ATF', flytta_pass: 'Flytta D-dag', passandring_notis: '🔔 Passändring' }
  const [aktiv, setAktiv] = useState('inkorg')
  const [angraDialog, setAngraDialog] = useState(null)

  useEffect(() => {
    const olästa = ansokningar.filter(arOlast)
    if (olästa.length > 0) {
      Promise.all(olästa.map(a =>
        supabase.from('ansokningar').update({ sedd: true }).eq('id', a.id)
      )).then(() => onLast && onLast())
    }
  }, [ansokningar])

  async function arkivera(a) {
    await supabase.from('ansokningar').update({
      arkiverad_medarbetare: true,
      arkiverad_medarbetare_at: new Date().toISOString()
    }).eq('id', a.id)
    onLast && onLast()
  }

  function angraAnsokan(a) {
    setAngraDialog(a)
  }

  async function bekraftaAngra() {
    const a = angraDialog
    setAngraDialog(null)
    await supabase.from('ansokningar').delete().eq('id', a.id)

    // Passbyte skapar en separat rad (informationsmeddelande) hos kollegan -
    // ta bort den också så den inte blir kvar och ser inaktuell/vilseledande ut.
    if (a.typ === 'passbyte') {
      await supabase.from('ansokningar').delete()
        .eq('skapad_av', a.skapad_av)
        .eq('typ', 'passandring_notis')
        .eq('passandring_fran', 'Passbytesbegäran')
        .eq('fran_datum', a.fran_datum)
        .eq('status', 'väntar')
    }

    onLast && onLast()
  }

  async function arkiveraAlla() {
    await Promise.all(arkiverbara.map(a => supabase.from('ansokningar').update({
      arkiverad_medarbetare: true,
      arkiverad_medarbetare_at: new Date().toISOString()
    }).eq('id', a.id)))
    onLast && onLast()
  }

  const inkorg = ansokningar.filter(a => !a.arkiverad_medarbetare)
  const arkiv = ansokningar.filter(a => a.arkiverad_medarbetare)
  const visaLista = aktiv === 'inkorg' ? inkorg : arkiv
  const arkiverbara = inkorg.filter(a => a.status !== 'väntar' || a.typ === 'passandring_notis' || a.passandring_fran === 'Passbytesbegäran')

  return (
    <div>
      {/* Flikar */}
      <div style={{ display: 'flex', borderBottom: '1.5px solid #f0f0f0' }}>
        {[
          { id: 'inkorg', label: '📥 Inkorg' },
          { id: 'arkiv', label: `🗄 Arkiv (${arkiv.length})` }
        ].map(f => (
          <button key={f.id} onClick={() => setAktiv(f.id)} style={{
            flex: 1, padding: '10px 0', border: 'none', background: 'none', cursor: 'pointer',
            fontWeight: aktiv === f.id ? 700 : 400,
            color: aktiv === f.id ? '#F11C0A' : '#aaa',
            borderBottom: aktiv === f.id ? '2px solid #F11C0A' : '2px solid transparent',
            fontSize: '.82rem', fontFamily: 'inherit'
          }}>{f.label}</button>
        ))}
      </div>

      <div style={{ padding: '10px 13px' }}>
        {aktiv === 'inkorg' && arkiverbara.length > 0 && (
          <button
            className="btn btn-secondary btn-sm"
            style={{ marginBottom: 10, width: '100%' }}
            onClick={arkiveraAlla}
          >
            🗄 Arkivera alla ({arkiverbara.length})
          </button>
        )}
        {visaLista.length === 0 ? (
          <p style={{ color: '#bbb', fontSize: '.8rem', textAlign: 'center', padding: '20px 0' }}>
            {aktiv === 'arkiv' ? 'Inga arkiverade ärenden' : 'Inga ansökningar än'}
          </p>
        ) : (
          visaLista.map(a => (
            <div key={a.id}
              onClick={() => a.fran_datum && onVisaDag && onVisaDag(a.fran_datum)}
              style={{
              background: '#fff',
              borderLeft: `3px solid ${a.status === 'beviljad' ? '#28a745' : a.status === 'nekad' ? '#dc3545' : '#ff9800'}`,
              borderRadius: 6, padding: '11px 13px', marginBottom: 8,
              cursor: a.fran_datum ? 'pointer' : undefined
            }}>
              {arOlast(a) && (
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#F11C0A', float: 'right', marginTop: 2 }} />
              )}
              <div style={{ fontWeight: 700, fontSize: '.86rem' }}>
                {a.passandring_fran === 'Passbytesbegäran'
                  ? `🔄 Passbytesbegäran från ${a.skapad_av_namn}`
                  : (a.passandring_fran === 'Passbyte beviljat' || a.passandring_fran === 'Passbyte nekat')
                  ? `🔄 ${a.passandring_fran} · ${a.passandring_till}`
                  : a.passandring_fran === 'SAP klar'
                  ? `✅ Inlagt i SAP · ${a.passandring_till}`
                  : a.typ === 'passandring_notis'
                  ? `🔔 Passändring ${new Date(a.fran_datum).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' })}`
                  : `${TYPER[a.typ]} · ${a.fran_datum}${a.till_datum && a.till_datum !== a.fran_datum ? ` – ${a.till_datum}` : ''}`
                }
              </div>
              <div style={{ fontSize: '.78rem', color: '#555', marginTop: 3 }}>
                {a.typ === 'passandring_notis' && (a.passandring_fran === 'Passbytesbegäran' || a.passandring_fran === 'Passbyte beviljat' || a.passandring_fran === 'Passbyte nekat' || a.passandring_fran === 'SAP klar')
                  ? <>{a.admin_kommentar}</>
                  : a.typ === 'passandring_notis'
                  ? <>
                      {a.passandring_fran} → {a.passandring_till}
                      {a.admin_kommentar && ` · ${a.admin_kommentar}`}
                      <div style={{ color: '#aaa', fontSize: '.72rem', marginTop: 2 }}>Ändrad av {a.skapad_av_namn} · Kontakta admin vid frågor</div>
                    </>
                  : <>
                      {a.status === 'beviljad' ? '✅ Beviljad' : a.status === 'nekad' ? '❌ Nekad' : '⏳ Väntar på svar'}
                      {a.admin_kommentar ? ` · "${a.admin_kommentar}"` : ''}
                    </>
                }
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                <div style={{ fontSize: '.7rem', color: '#bbb' }}>{new Date(a.skapad_at).toLocaleString('sv-SE')}</div>
                <div style={{ display: 'flex', gap: 10 }}>
                  {a.status === 'väntar' && a.typ !== 'passandring_notis' && a.passandring_fran !== 'Passbytesbegäran' && (
                    <button
                      style={{ fontSize: '.68rem', color: '#c0392b', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px' }}
                      onClick={e => { e.stopPropagation(); angraAnsokan(a) }}
                    >↩ Ångra</button>
                  )}
                  {!a.arkiverad_medarbetare && (a.status !== 'väntar' || a.typ === 'passandring_notis' || a.passandring_fran === 'Passbytesbegäran') && (
                    <button
                      style={{ fontSize: '.68rem', color: '#bbb', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px' }}
                      onClick={e => { e.stopPropagation(); arkivera(a) }}
                    >🗄 Arkivera</button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
        {aktiv === 'arkiv' && arkiv.length > 0 && (
          <p style={{ color: '#ddd', fontSize: '.7rem', textAlign: 'center', marginTop: 10 }}>
            Arkiverade ärenden raderas automatiskt efter 6 månader
          </p>
        )}
      </div>
      {angraDialog && (
        <BekraftaModal
          titel="Ångra ansökan"
          meddelande="Vill du verkligen ångra och ta bort den här ansökan?"
          farligKnapp
          onBekrafta={bekraftaAngra}
          onAvbryt={() => setAngraDialog(null)}
        />
      )}
    </div>
  )
}

// Byt ut mot din riktiga Access Key från web3forms.com när du har den
const WEB3FORMS_ACCESS_KEY = '23f9d09c-11ed-4b22-8bc3-9a69f4546599'

// Din publika VAPID-nyckel (säker att ha i koden, den privata ligger bara i Supabase)
const VAPID_PUBLIC_KEY = 'BGd6XazySeddDvrHv4odQcAut0u-NUiFOAskrSbkEvDOISxnQhbU1PTsc3isaLWiQ4C59vlmHmHFe5IE9aYURgQ'

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)))
}

// Passbeskrivningar per grupp. Bara DISP har beskrivningar än så länge.
const PASS_GRUPPER = ['RN', 'LN MMÖ', 'LN NKP', 'DISP']
const PASSBESKRIVNINGAR = {
  DISP: [
    { pass: 'F', tid: '06.30–15.00', timmar: '8,5', kommentar: 'operativt' },
    { pass: 'E', tid: '14.30–23.00', timmar: '8,5', kommentar: 'operativt' },
    { pass: 'N', tid: '22.30–07.00', timmar: '8,5', kommentar: 'operativt' },
    { pass: 'DT', tid: '07.00–15.30', timmar: '8,5', kommentar: 'operativt', tidigare: 'D1' },
    { pass: 'V', tid: '08.00–16.30', timmar: '8', kommentar: 'vilodag' },
    { pass: 'D', tid: '08.00–16.30', timmar: '8', kommentar: 'kontorsdag' },
    { pass: 'D4', tid: '08.00–16.30', timmar: '8', kommentar: 'admin plan. avbrott/DO' },
    { pass: 'FU', tid: '08.00–16.30', timmar: '8', kommentar: 'förtroendeuppdrag' },
    { pass: 'STAT', tid: '08.00–16.30', timmar: '8', kommentar: 'statistik' },
    { pass: 'DB', tid: '07.00–15.30', timmar: '8', kommentar: 'dagtid beredskap' },
  ]
}

function ProfilVy({ medarbetare, onLogout }) {
  const [visaPass, setVisaPass] = React.useState(false)
  const [passGrupp, setPassGrupp] = React.useState(PASS_GRUPPER.includes(medarbetare?.avdelning) ? medarbetare.avdelning : 'DISP')
  const [visaFeedback, setVisaFeedback] = React.useState(false)
  const [visaHjalp, setVisaHjalp] = React.useState(false)
  const [feedbackText, setFeedbackText] = React.useState('')
  const [skickar, setSkickar] = React.useState(false)
  const [skickat, setSkickat] = React.useState(false)
  const [notisStatus, setNotisStatus] = React.useState('okand') // okand | av | pa | stods_ej | nekad
  const [notisLaddar, setNotisLaddar] = React.useState(false)
  const [morkt, setMorkt] = React.useState(lasMorktLage())

  React.useEffect(() => {
    let klar = false

    async function kollaStatus() {
      try {
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
          setNotisStatus('stods_ej')
          return
        }
        if (Notification.permission === 'denied') {
          setNotisStatus('nekad')
          return
        }
        const reg = await navigator.serviceWorker.ready
        const sub = await reg.pushManager.getSubscription()
        klar = true
        setNotisStatus(sub ? 'pa' : 'av')
      } catch (err) {
        console.error('Kunde inte kolla notisstatus:', err)
        klar = true
        setNotisStatus('av')
      }
    }
    kollaStatus()

    // Säkerhetsgräns: hänger kollen kvar (t.ex. service worker som aldrig blir redo)
    // visar vi ändå knappen efter 4 sekunder istället för att fastna för evigt
    const timeout = setTimeout(() => {
      if (!klar) setNotisStatus('av')
    }, 4000)
    return () => clearTimeout(timeout)
  }, [])

  async function aktiveraNotiser() {
    setNotisLaddar(true)
    try {
      const tillstand = await Notification.requestPermission()
      if (tillstand !== 'granted') {
        setNotisStatus(tillstand === 'denied' ? 'nekad' : 'av')
        return
      }
      const timeoutLoft = new Promise((_, avvisa) =>
        setTimeout(() => avvisa(new Error('Det tog för lång tid. Försök igen.')), 5000)
      )
      const skaffaSub = (async () => {
        const reg = await navigator.serviceWorker.ready
        return reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
        })
      })()
      const sub = await Promise.race([skaffaSub, timeoutLoft])
      const subJson = sub.toJSON()
      await supabase.from('push_prenumerationer').upsert({
        medarbetare_id: medarbetare.id,
        endpoint: subJson.endpoint,
        p256dh: subJson.keys.p256dh,
        auth: subJson.keys.auth
      }, { onConflict: 'medarbetare_id,endpoint' })
      setNotisStatus('pa')
    } catch (err) {
      console.error('Kunde inte aktivera notiser:', err)
      alert('Kunde inte aktivera notiser: ' + (err?.message || 'okänt fel'))
    } finally {
      setNotisLaddar(false)
    }
  }

  async function stangAvNotiser() {
    setNotisLaddar(true)
    try {
      const reg = await navigator.serviceWorker.ready
      const sub = await reg.pushManager.getSubscription()
      if (sub) {
        await supabase.from('push_prenumerationer').delete().eq('endpoint', sub.endpoint)
        await sub.unsubscribe()
      }
      setNotisStatus('av')
    } catch (err) {
      console.error('Kunde inte stänga av notiser:', err)
    } finally {
      setNotisLaddar(false)
    }
  }

  async function skickaFeedback() {
    if (!feedbackText.trim()) return
    setSkickar(true)
    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_key: WEB3FORMS_ACCESS_KEY,
          subject: 'Feedback – E.ON Schema',
          from_name: `${medarbetare?.fornamn || ''} ${medarbetare?.efternamn || ''}`.trim() || 'Okänd medarbetare',
          message: feedbackText.trim()
        })
      })
      const data = await res.json()
      if (data.success) {
        setSkickat(true)
        setFeedbackText('')
      } else {
        alert('Kunde inte skicka feedback just nu, försök igen senare.')
      }
    } catch {
      alert('Kunde inte skicka feedback just nu, försök igen senare.')
    } finally {
      setSkickar(false)
    }
  }

  return (
    <div style={{ padding: '30px 16px', textAlign: 'center', color: '#aaa' }}>
      <EonLogo size={52} borderRadius={12} style={{ margin: '0 auto 12px' }} />
      <div style={{ fontWeight: 700, color: '#444', fontSize: '.9rem', marginBottom: 4 }}>{medarbetare?.fornamn} {medarbetare?.efternamn}</div>
      <div style={{ fontSize: '.8rem', marginBottom: 4 }}>{medarbetare?.avdelning}</div>
      <div style={{ fontSize: '.8rem', marginBottom: 20 }}>KID: {medarbetare?.kid}</div>

      {notisStatus === 'okand' && (
        <div style={{ fontSize: '.78rem', color: '#bbb' }}>Kollar notisinställningar...</div>
      )}
      {notisStatus === 'pa' && (
        <div>
          <div className="info-box info-box-green" style={{ display: 'inline-block', fontSize: '.78rem' }}>🔔 Notiser aktiverade</div>
          <div onClick={stangAvNotiser} style={{ marginTop: 6, fontSize: '.75rem', color: '#999', textDecoration: 'underline', cursor: 'pointer' }}>
            {notisLaddar ? 'Stänger av...' : 'Stäng av notiser'}
          </div>
        </div>
      )}
      {notisStatus === 'av' && (
        <button className="btn btn-primary btn-sm" onClick={aktiveraNotiser} disabled={notisLaddar}>
          {notisLaddar ? 'Aktiverar...' : '🔔 Aktivera notiser'}
        </button>
      )}
      {notisStatus === 'nekad' && (
        <div className="info-box info-box-yellow" style={{ fontSize: '.78rem' }}>
          🔕 Notiser är blockerade i telefonens inställningar. Gå till webbläsarens sidinställningar för att tillåta dem.
        </div>
      )}
      {notisStatus === 'stods_ej' && (
        <div className="info-box info-box-blue" style={{ fontSize: '.78rem' }}>
          Notiser stöds inte i den här webbläsaren ännu.
        </div>
      )}

      <div
        onClick={() => setVisaHjalp(true)}
        style={{ marginTop: 18, fontSize: '.8rem', color: '#999', textDecoration: 'underline', cursor: 'pointer' }}
      >
        ❓ Färgförklaring & hjälp
      </div>

      <div
        onClick={() => setVisaPass(true)}
        style={{ marginTop: 12, fontSize: '.8rem', color: '#999', textDecoration: 'underline', cursor: 'pointer' }}
      >
        🕒 Passbeskrivning
      </div>

      <a
        href="/manual.pdf"
        target="_blank"
        rel="noopener"
        style={{ display: 'block', marginTop: 12, fontSize: '.8rem', color: '#999', textDecoration: 'underline', cursor: 'pointer' }}
      >
        📄 Manual (PDF)
      </a>

      <div
        onClick={() => { setVisaFeedback(true); setSkickat(false) }}
        style={{ marginTop: 12, fontSize: '.8rem', color: '#999', textDecoration: 'underline', cursor: 'pointer' }}
      >
        💬 Har du feedback eller hittat en bugg?
      </div>

      <div style={{ textAlign: 'center' }}>
      <div
        role="switch"
        aria-checked={morkt}
        onClick={() => { const nytt = !morkt; setMorkt(nytt); sattMorktLage(nytt) }}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 10,
          marginTop: 26, padding: '8px 14px',
          background: '#fff', border: '1.5px solid #e4e4e4', borderRadius: 20,
          cursor: 'pointer', userSelect: 'none'
        }}
      >
        <span style={{ fontSize: '.88rem', fontWeight: 600, color: '#444' }}>Mörkt läge</span>
        <span style={{
          position: 'relative', width: 38, height: 22, borderRadius: 11, flexShrink: 0,
          background: morkt ? '#F11C0A' : '#d5d5d5', transition: 'background .2s'
        }}>
          <span style={{
            position: 'absolute', top: 3, left: morkt ? 19 : 3, width: 16, height: 16,
            borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,.3)',
            transition: 'left .2s'
          }} />
        </span>
      </div>
      </div>

      <button className="btn btn-secondary" onClick={onLogout} style={{ margin: '14px auto 0' }}>Logga ut</button>

      {visaPass && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setVisaPass(false)}>
          <div className="modal-box" style={{ maxWidth: 380, maxHeight: '85vh' }}>
            <div className="modal-header">
              <h3>🕒 Passbeskrivning</h3>
              <button className="modal-close" onClick={() => setVisaPass(false)}>×</button>
            </div>
            <div className="modal-body" style={{ textAlign: 'left' }}>
              <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
                {PASS_GRUPPER.map(g => (
                  <button
                    key={g}
                    type="button"
                    onClick={() => setPassGrupp(g)}
                    style={{
                      padding: '6px 12px', borderRadius: 16, cursor: 'pointer', fontFamily: 'inherit',
                      fontSize: '.78rem', fontWeight: 600,
                      border: `1.5px solid ${passGrupp === g ? '#F11C0A' : '#e0e0e0'}`,
                      background: passGrupp === g ? '#F11C0A' : '#fff',
                      color: passGrupp === g ? '#fff' : '#555'
                    }}
                  >{g}</button>
                ))}
              </div>

              {PASSBESKRIVNINGAR[passGrupp] ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {PASSBESKRIVNINGAR[passGrupp].map(p => (
                    <div key={p.pass} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '8px 10px', background: '#f7f7f7', borderRadius: 8 }}>
                      <div style={{ minWidth: 44, fontWeight: 800, fontSize: '.95rem', color: '#333' }}>
                        {p.pass}
                      </div>
                      <div style={{ fontSize: '.8rem', color: '#555', lineHeight: 1.5 }}>
                        <div><strong style={{ color: '#333' }}>{p.tid}</strong></div>
                        <div>{p.kommentar}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ textAlign: 'center', color: '#999', fontSize: '.85rem', margin: '24px 0' }}>
                  Passbeskrivning för {passGrupp} kommer inom kort.
                </p>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary btn-full" onClick={() => setVisaPass(false)}>Stäng</button>
            </div>
          </div>
        </div>
      )}

      {visaHjalp && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setVisaHjalp(false)}>
          <div className="modal-box" style={{ maxWidth: 380, maxHeight: '85vh' }}>
            <div className="modal-header">
              <h3>❓ Färgförklaring & hjälp</h3>
              <button className="modal-close" onClick={() => setVisaHjalp(false)}>×</button>
            </div>
            <div className="modal-body" style={{ textAlign: 'left' }}>
              <div style={{ fontSize: '.72rem', fontWeight: 700, color: '#bbb', letterSpacing: '.5px', marginBottom: 8 }}>FÄRGER I SCHEMAT</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginBottom: 20 }}>
                {Object.entries(KATEGORI_FARGER)
                  .filter(([key]) => key !== 'passandring')
                  .map(([key, farg]) => (
                    <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{
                        width: 22, height: 22, borderRadius: 5, flexShrink: 0,
                        background: farg.bg === 'transparent' ? '#fff' : farg.bg,
                        border: `1.5px solid ${farg.bg === 'transparent' ? '#ddd' : farg.text}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '.6rem', color: farg.text, fontWeight: 700
                      }}>
                        {key.startsWith('beviljad_') && <span style={{ color: '#2e7d32', fontSize: '.75rem' }}>✦</span>}
                      </div>
                      <span style={{ fontSize: '.82rem', color: '#444' }}>{farg.label}</span>
                    </div>
                  ))}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.85rem' }}>⭐</div>
                  <span style={{ fontSize: '.82rem', color: '#444' }}>Passändring – kan ge skiftersättning</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 22, display: 'flex', justifyContent: 'center', flexShrink: 0 }}><span style={{ fontWeight: 800, fontSize: '.8rem', color: '#F11C0A', borderBottom: '3px solid #d32f2f' }}>D</span></div>
                  <span style={{ fontSize: '.82rem', color: '#444' }}>Rött streck under passnamnet – beredskap</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 22, display: 'flex', justifyContent: 'center', flexShrink: 0 }}><span style={{ fontWeight: 800, fontSize: '.8rem', color: '#F11C0A', borderBottom: '3px solid #1976d2' }}>D</span></div>
                  <span style={{ fontSize: '.82rem', color: '#444' }}>Blått streck under passnamnet – bokad dag</span>
                </div>
              </div>

              <div style={{ fontSize: '.72rem', fontWeight: 700, color: '#bbb', letterSpacing: '.5px', marginBottom: 8 }}>SNABBFAKTA</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: '.8rem', color: '#555', lineHeight: 1.5 }}>
                <div>
                  <strong style={{ color: '#333' }}>🏖 Ledighetsansökan</strong><br />
                  Ansök om semester eller föräldraledighet (kryssruta i formuläret) för valfritt datumintervall.
                </div>
                <div>
                  <strong style={{ color: '#333' }}>🔄 Passbyte</strong><br />
                  Byt pass med en kollega i samma avdelning. Gäller bytet flera pass, kryssa i "Passbytet gäller flera pass" och välj dagarna i kalendrarna. Dagar där du har ansökt om ledighet går inte att välja. Gäller det ett mer komplicerat byte, beskriv det i kommentarsfältet så tar schemaansvarig hand om det.
                </div>
                <div>
                  <strong style={{ color: '#333' }}>📌 Boka dag</strong><br />
                  Ansök om att boka upp dig på en dag för t.ex. möten, fältbesök eller liknande.
                </div>
                <div>
                  <strong style={{ color: '#333' }}>⏱ ATF</strong><br />
                  Registrera arbetstidsförkortning med start- och sluttid. Gäller inte heldag; då skickar du istället in en ledighetsansökan.
                </div>
                <div>
                  <strong style={{ color: '#333' }}>⭐ Stjärna på ett pass</strong><br />
                  Betyder att passet ändrats från det ursprungligen planerade. Klicka på dagen för att se ändringsloggen och eventuell skiftersättning.
                </div>
                <div>
                  <strong style={{ color: '#333' }}>📅 Flytta D-dag</strong><br />
                  Har du en D-dag kan du ansöka om att flytta den. Tryck på dagen och välj Flytta D-dag, ange ny dag och orsak.
                </div>
                <div>
                  <strong style={{ color: '#333' }}>🟨 Gul dag med S framför passet</strong><br />
                  Din ledighetsansökan väntar på svar. När den är beviljad blir dagen gul med en grön <span style={{ color: '#2e7d32', fontWeight: 700 }}>✦</span>.
                </div>
                <div>
                  <strong style={{ color: '#333' }}>👥 Helhetsschemat</strong><br />
                  Tryck på en ruta för att se ändringarna som gjorts på passet och interna noteringar. Med "Ladda ner Excel" får du månaden som fil med noteringarna i rutorna.
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary btn-full" onClick={() => setVisaHjalp(false)}>Stäng</button>
            </div>
          </div>
        </div>
      )}

      {visaFeedback && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setVisaFeedback(false)}>
          <div className="modal-box" style={{ maxWidth: 340 }}>
            <div className="modal-header">
              <h3>💬 Feedback</h3>
              <button className="modal-close" onClick={() => setVisaFeedback(false)}>×</button>
            </div>
            <div className="modal-body" style={{ textAlign: 'left' }}>
              {skickat ? (
                <div className="info-box info-box-green">
                  Tack! Din feedback är skickad. 🎉
                </div>
              ) : (
                <>
                  <label className="form-label">Vad vill du berätta?</label>
                  <textarea
                    className="form-control"
                    rows={5}
                    placeholder="Buggar, önskemål, eller vad som helst..."
                    value={feedbackText}
                    onChange={e => setFeedbackText(e.target.value)}
                  />
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setVisaFeedback(false)}>
                {skickat ? 'Stäng' : 'Avbryt'}
              </button>
              {!skickat && (
                <button className="btn btn-primary" onClick={skickaFeedback} disabled={skickar || !feedbackText.trim()}>
                  {skickar ? 'Skickar...' : 'Skicka'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function KollegaSchema({ kollega, allaPass, pass, kollegaManad, setKollegaManad, kollegaDatum, setKollegaDatum, visaLedigaDagar, blockerade, tidigastDatum, tomtLage, flerval, onToggle, lasta }) {
  const ar = kollegaManad.getFullYear()
  const man = kollegaManad.getMonth()
  const antalDagar = new Date(ar, man + 1, 0).getDate()
  let startDow = new Date(ar, man, 1).getDay()
  startDow = startDow === 0 ? 6 : startDow - 1
  const manadNamn = kollegaManad.toLocaleDateString('sv-SE', { month: 'long', year: 'numeric' })
  const dagNamn = ['M','T','O','T','F','L','S']

  const valdtPass = allaPass.find(pp => pp.medarbetare_id === kollega && pp.datum === kollegaDatum)

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <button onClick={() => setKollegaManad(new Date(ar, man - 1, 1))} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#555', padding: '0 4px' }}>‹</button>
        <span style={{ fontSize: '.78rem', color: '#888', fontWeight: 600, textTransform: 'capitalize' }}>{manadNamn}</span>
        <button onClick={() => setKollegaManad(new Date(ar, man + 1, 1))} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#555', padding: '0 4px' }}>›</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 10 }}>
        {dagNamn.map((d, i) => (
          <div key={i} style={{ textAlign: 'center', fontSize: 9, color: '#bbb', fontWeight: 700, paddingBottom: 3 }}>{d}</div>
        ))}
        {Array.from({ length: startDow }).map((_, i) => (
          <div key={`e-${i}`} />
        ))}
        {Array.from({ length: antalDagar }).map((_, i) => {
          const dag = i + 1
          const dat = `${ar}-${String(man + 1).padStart(2,'0')}-${String(dag).padStart(2,'0')}`
          const p = allaPass.find(pp => pp.medarbetare_id === kollega && pp.datum === dat)
          const mittEgetPass = pass.find(pp => pp.datum === dat)
          const namn = (p?.passtyp_namn || '').toUpperCase()
          const erLedig = !namn || 
            ['X','SX','LX','SJX','V'].includes(namn) || 
            namn.startsWith('L') || 
            namn.startsWith('S') ||
            (p?.kategori === 'passbyte' && !namn) ||
            ['ledig','semester','beviljad_semester','foraldraledighet','beviljad_foraldraledighet'].includes(p?.kategori)
          const erLast = !!lasta?.includes(dat)
          const erBlockerad = !erLast && (blockerade?.includes(dat) || (!!tidigastDatum && dat < tidigastDatum))
          // Om visaLedigaDagar=false: bara ordinarie pass klickbara
          // Om visaLedigaDagar=true: alla dagar klickbara utom X/SX
          const kanKlicka = !erLast && !erBlockerad && (visaLedigaDagar ? (!['X','SX','LX','SJX'].includes(namn)) : (p && !erLedig))
          const vald = (flerval ? flerval.includes(dat) : dat === kollegaDatum) && !erLast
          const bg = erLast ? '#ffcc80' : vald ? '#F11C0A' : erBlockerad ? '#ffebee' : 
            p && !erLedig ? '#e3f2fd' : 
            p && (namn.startsWith('S') || namn.startsWith('L') || namn === 'V') ? '#fffde7' :
            visaLedigaDagar ? '#f5f5f5' : 'transparent'
          return (
            <div key={dat}
              onClick={() => { if (kanKlicka) { if (flerval && onToggle) onToggle(dat); else setKollegaDatum(dat) } }}
              title={erBlockerad ? (tomtLage ? 'Dagen kan inte väljas' : 'Du jobbar redan denna dag') : ''}
              style={{
                textAlign: 'center', borderRadius: 4, padding: '3px 1px',
                cursor: kanKlicka ? 'pointer' : 'not-allowed',
                background: bg,
                border: erLast ? '1.5px solid #fb8c00' : vald ? '1.5px solid #c62828' : '1.5px solid transparent',
                opacity: erBlockerad ? 0.5 : 1
              }}>
              <div style={{ fontSize: 9, color: erLast ? '#5d4037' : vald ? '#fff' : p && !erLedig ? '#1565c0' : '#aaa', fontWeight: p && !erLedig ? 700 : 400 }}>
                {dag}
              </div>
              <div style={{ fontSize: 8, fontWeight: 700, color: erLast ? '#5d4037' : vald ? '#fff' : p && !erLedig ? '#1565c0' : p ? '#aaa' : '#bbb' }}>
                {p ? p.passtyp_namn : visaLedigaDagar ? '–' : ''}
              </div>
            </div>
          )
        })}
      </div>

      {flerval ? null : tomtLage ? (
        kollegaDatum ? (
          <div style={{ background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 5, padding: '8px 10px', marginBottom: 8, fontSize: '.82rem' }}>
            <span style={{ color: '#555' }}>Flyttas till: </span>
            <strong>{new Date(kollegaDatum + 'T12:00:00').toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'long' })}</strong>
          </div>
        ) : (
          <div style={{ color: '#aaa', fontSize: '.78rem', marginBottom: 8 }}>Klicka på en ledig dag ovan</div>
        )
      ) : valdtPass ? (
        <div style={{ background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 5, padding: '8px 10px', marginBottom: 8, fontSize: '.82rem' }}>
          <span style={{ color: '#555' }}>Valt: </span>
          <strong>{new Date(kollegaDatum+'T12:00:00').toLocaleDateString('sv-SE', {day:'numeric',month:'short'})} · {valdtPass.passtyp_namn}</strong>
        </div>
      ) : (
        <div style={{ color: '#aaa', fontSize: '.78rem', marginBottom: 8 }}>Klicka på ett pass ovan</div>
      )}
    </>
  )
}


function AnsokningsModal({ typ, medarbetareId, avdelning, skapadAvNamn, mittPass, valdDag, allaMedarbetare, allaPass, pass, onSend, onClose }) {
  const startDatum = valdDag || idagLokal()
  const [fran, setFran] = useState(startDatum)
  const [till, setTill] = useState(startDatum)
  const [kommentar, setKommentar] = useState('')
  const [onskaD, setOnskaD] = useState(false)
  const [valdKollega, setValdKollega] = useState('')
  // Flera pass i samma passbyte
  const [flerLage, setFlerLage] = useState(false)
  const [minaValda, setMinaValda] = useState([])      // mina pass som byts bort
  const [kollegaValda, setKollegaValda] = useState([]) // kollegans pass jag tar (utöver de som byts samma dag)
  const [minManad, setMinManad] = useState(() => { const d = new Date(startDatum); return new Date(d.getFullYear(), d.getMonth(), 1) })
  const [kollegaDatum, setKollegaDatum] = useState(null) // dagen kollegan väljs
  const [kollegaManad, setKollegaManad] = useState(() => {
    const d = new Date(startDatum)
    return new Date(d.getFullYear(), d.getMonth(), 1)
  })
  const [jagJobbarDatum, setJagJobbarDatum] = useState(null) // om kollegan är ledig - dag jag jobbar åt kollegan
  const [jagJobbarManad, setJagJobbarManad] = useState(() => {
    const d = new Date(startDatum)
    return new Date(d.getFullYear(), d.getMonth(), 1)
  })
  const [skickar, setSkickar] = useState(false)
  const [arForaldraledighet, setArForaldraledighet] = useState(false)
  const [atfFran, setAtfFran] = useState('')
  const [atfTill, setAtfTill] = useState('')
  const [atfFelVisas, setAtfFelVisas] = useState(false)
  const [ansokFel, setAnsokFel] = useState('') // felmeddelande under formuläret (ersätter webbläsarrutor)
  useEffect(() => { setAnsokFel('') }, [kommentar, kollegaDatum, typ])
  // Flytta D-dag: egna pass från idag och ett par år framåt (så vi vet exakt vilka dagar som är lediga)
  const [flyttPass, setFlyttPass] = useState(null)
  useEffect(() => {
    if (typ !== 'flytta_pass') return
    let avbruten = false
    async function hamtaEgna() {
      const start = idagLokal()
      const slut = `${Number(start.slice(0, 4)) + 2}-12-31`
      const { data } = await supabase
        .from('pass')
        .select('medarbetare_id, datum, passtyp_namn, kategori, gul_ansokan_id')
        .eq('medarbetare_id', medarbetareId)
        .gte('datum', start)
        .lte('datum', slut)
      if (!avbruten) setFlyttPass(data || [])
    }
    hamtaEgna()
    return () => { avbruten = true }
  }, [typ, medarbetareId])

  const TITLAR = { ledighet: '🏖 Ledighetsansökan', foraldraledighet: '👶 Föräldraledighet', passbyte: '🔄 Passbyte', boka_dag: '📌 Boka dag', atf: '⏱ ATF', flytta_pass: '📅 Flytta D-dag' }

  // Kollegor för passbyte (bara från samma avdelning, exkl sig själv)
  const kollegor = allaMedarbetare.filter(m => m.id !== medarbetareId && m.avdelning === avdelning)

  // Kollegans pass på valt datum
  const kollegansPass = kollegaDatum ? allaPass.find(p => p.medarbetare_id === valdKollega && p.datum === kollegaDatum) : null
  const kollegansPassNamn = (kollegansPass?.passtyp_namn || '').toUpperCase()
  const kolleganErLedig = kollegaDatum && (!kollegansPass || 
    ['X','SX','LX','SJX','V'].includes(kollegansPassNamn) || 
    kollegansPassNamn.startsWith('S') || 
    kollegansPassNamn.startsWith('L') ||
    ['ledig','semester','beviljad_semester','foraldraledighet','beviljad_foraldraledighet'].includes(kollegansPass?.kategori))
  const kollegansPassPaFran = allaPass.find(p => p.medarbetare_id === valdKollega && p.datum === fran)
  
  // ── Flera pass i samma passbyte (en kollega, lika många pass på båda sidor) ──
  const arJobbNamn = n => { const u = (n || '').toUpperCase(); return !!u && !['X','SX','LX','SJX','V'].includes(u) && !u.startsWith('L') && !u.startsWith('S') }
  const arLedigKat = k => ['ledig','semester','beviljad_semester','foraldraledighet','beviljad_foraldraledighet'].includes(k)
  const idagStr = idagLokal()
  const minaJobb = pass.filter(p => arJobbNamn(p.passtyp_namn) && !arLedigKat(p.kategori) && p.datum >= idagStr)
  const minaJobbDatum = new Set(minaJobb.map(p => p.datum))
  // Dagar där jag sökt (eller fått beviljad) ledighet via ansökan - dessa får inte tas över åt en kollega.
  // (Vanliga semesterdagar från schemat, t.ex. SS, är OK eftersom man är ledig från början.)
  // Räknas även ledighet lagd direkt i schemat på ett arbetspass (SE, SF, SX, LE, LX osv). Bara ren semester
  // från början (SS) spärras inte.
  const arLedigPaArbetspass = p => {
    if (!['semester','beviljad_semester','foraldraledighet','beviljad_foraldraledighet','permission','flex'].includes(p.kategori)) return false
    const n = (p.passtyp_namn || '').toUpperCase().trim()
    if (!(n.startsWith('S') || n.startsWith('L'))) return false
    const bas = n.slice(1)
    return bas.length > 0 && bas !== 'S'
  }
  // Veckovila (V) och X-dagar får inte heller tas över åt en kollega
  const ansoktLedigt = pass.filter(p => p.gul_ansokan_id || arLedigPaArbetspass(p) || ['V', 'X'].includes((p.passtyp_namn || '').toUpperCase().trim())).map(p => p.datum)
  const kollegaJobb = allaPass.filter(p => p.medarbetare_id === valdKollega && arJobbNamn(p.passtyp_namn) && !arLedigKat(p.kategori) && p.datum >= idagStr)
  const kollegaJobbDatum = new Set(kollegaJobb.map(p => p.datum))
  // Samma regel som förr: jobbar kollegan samma dag byts den dagen med varandra,
  // annars måste kollegan jobba en dag där jag är ledig.
  const tvingade = [...minaValda].filter(d => kollegaJobbDatum.has(d)).sort()
  const behov = minaValda.filter(d => !kollegaJobbDatum.has(d)).length
  const flerKlart = flerLage && minaValda.length > 0 && !!valdKollega && kollegaValda.length === behov
  const kortDatum = d => new Date(d + 'T12:00:00').toLocaleDateString('sv-SE', { weekday: 'short', day: 'numeric', month: 'short' })
  const minNamn = d => minaJobb.find(p => p.datum === d)?.passtyp_namn || ''
  const kollNamn = d => kollegaJobb.find(p => p.datum === d)?.passtyp_namn || ''
  const minaAlla = [...minaValda].sort()
  const kollegaAlla = [...tvingade, ...kollegaValda].sort()
  const franEff = flerLage ? (minaAlla[0] || fran) : fran
  const tillEff = flerLage ? (kollegaAlla[0] || kollegaDatum) : kollegaDatum
  function bytMinDag(d) { setMinaValda(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]); setKollegaValda([]) }
  function bytKollegaDag(d) {
    setKollegaValda(prev => prev.includes(d) ? prev.filter(x => x !== d) : (prev.length < behov ? [...prev, d] : prev))
  }

  // Pass jag jobbar åt kollegan (om kollegan är ledig)
  const mittPassPaJagJobbarDatum = jagJobbarDatum ? pass.find(p => p.datum === jagJobbarDatum) : null

  // Ledighet får inte sökas bakåt i tiden, och till-datum får inte vara före från-datum
  const ledighetFel = typ !== 'ledighet' ? '' :
    (!fran || !till) ? 'Välj både från- och till-datum.' :
    fran < idagLokal() ? 'Du kan inte ansöka om ledighet bakåt i tiden.' :
    till < fran ? 'Till-datum kan inte vara före från-datum.' : ''

  // ATF: båda tiderna måste fyllas i och vara giltiga (visas under fälten först när man försökt skicka)
  const atfFel = typ !== 'atf' ? '' :
    (!atfFran || !atfTill) ? 'Fyll i både från- och tilltid.' :
    (!arGiltigTid(atfFran) || !arGiltigTid(atfTill)) ? 'Skriv en giltig tid, t.ex. 13:00.' : ''

  async function skicka() {
    if (ledighetFel) return
    if (atfFel) { setAtfFelVisas(true); return }
    if (typ === 'boka_dag' && !kommentar.trim()) {
      setAnsokFel('Kommentar är obligatorisk för Boka dag.')
      return
    }
    if (typ === 'flytta_pass' && !kollegaDatum) {
      setAnsokFel('Välj vilken dag du vill flytta din D-dag till.')
      return
    }
    if (typ === 'flytta_pass' && !kommentar.trim()) {
      setAnsokFel('Kommentar är obligatorisk när du flyttar en D-dag.')
      return
    }
    setSkickar(true)
    // Kryssrutan avgör om detta ska sparas som föräldraledighet istället för vanlig ledighet
    const verkligTyp = (typ === 'ledighet' && arForaldraledighet) ? 'foraldraledighet' : typ

    // Bygg kommentar med passbytesinformation
    const kollega = allaMedarbetare.find(m => m.id === valdKollega)
    const kollegaNamn = kollega ? `${kollega.fornamn} ${kollega.efternamn}` : ''
    const listaTxt = (lista, namnFn) => lista.map(d => `${kortDatum(d)} (${namnFn(d)})`).join(', ')
    const avsFornamn = skapadAvNamn.split(' ')[0]
    const kollFornamn = kollega?.fornamn || 'kollegan'
    const passbyteInfo = typ === 'passbyte' && kollega && flerLage
      ? `Byter ${minaAlla.length} pass med ${kollegaNamn} · ${kollFornamn} tar ${agsNamn(avsFornamn)} pass: ${listaTxt(minaAlla, minNamn)} · ${avsFornamn} tar ${agsNamn(kollFornamn)} pass: ${listaTxt(kollegaAlla, kollNamn)}${kommentar ? ` · ${kommentar}` : ''}`
      : typ === 'passbyte' && kollega
      ? `Mitt pass: ${fran} · Kollegans pass: ${kollegaDatum} · Byter med: ${kollegaNamn}${kollegansPass ? ` (${kollegansPass.passtyp_namn})` : ''}${kommentar ? ` · ${kommentar}` : ''}`
      : typ === 'flytta_pass'
        ? `Flytt av D-dag på egen begäran: ${fran} → ${kollegaDatum} · ${kommentar}`
        : typ === 'boka_dag' && onskaD
          ? `Önskar D-dag denna dag · ${kommentar}`
          : kommentar || null

    const { error: ansokanError } = await supabase.from('ansokningar').insert({
      typ: verkligTyp,
      skapad_av: medarbetareId,
      skapad_av_namn: skapadAvNamn,
      avdelning,
      fran_datum: franEff || null,
      till_datum: typ === 'ledighet' ? (till || fran) : typ === 'flytta_pass' ? kollegaDatum : typ === 'passbyte' && flerLage ? tillEff : fran,
      medarbetare_kommentar: passbyteInfo,
      atf_fran_tid: typ === 'atf' ? atfFran : null,
      atf_till_tid: typ === 'atf' ? atfTill : null
    })
    if (ansokanError) {
      alert('DEBUG – kunde inte skicka ansökan: ' + ansokanError.message)
      setSkickar(false)
      return
    }

    // Skicka notis till kollegan vid passbyte
    if (typ === 'passbyte' && valdKollega) {
      const avsandarFornamn = skapadAvNamn.split(' ')[0]
      const kollegaInfo = flerLage
        ? `${skapadAvNamn} vill byta ${minaAlla.length} pass med dig. ${avsandarFornamn} tar dina pass: ${listaTxt(kollegaAlla, kollNamn)}. Du tar ${agsNamn(avsandarFornamn)} pass: ${listaTxt(minaAlla, minNamn)}.${kommentar ? ` Kommentar: ${kommentar}` : ''}`
        : `${skapadAvNamn} vill byta pass med dig: ${avsandarFornamn} jobbar ditt ${kollegansPass?.passtyp_namn || ''} (${kollegaDatum}), du jobbar ${avsandarFornamn}s ${mittPass?.passtyp_namn || ''} (${fran}).${kommentar ? ` Kommentar: ${kommentar}` : ''}`
      await supabase.from('ansokningar').insert({
        typ: 'passandring_notis',
        skapad_av: medarbetareId,
        skapad_av_namn: skapadAvNamn,
        avdelning,
        fran_datum: franEff,
        till_datum: tillEff,
        mottagare_id: valdKollega,
        admin_kommentar: kollegaInfo,
        status: 'väntar',
        passandring_fran: 'Passbytesbegäran',
        passandring_till: skapadAvNamn,
        arkiverad_medarbetare: false,
        sedd: false
      })
    }

    setSkickar(false)
    onSend()
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth: 320 }}>
        <div className="modal-header">
          <h3>{TITLAR[typ]}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {typ === 'ledighet' && (
            <>
              <div className="form-row">
                <div><label className="form-label">Från</label><input className="form-control" type="date" value={fran} min={idagLokal()} onChange={e => { setFran(e.target.value); if (till < e.target.value) setTill(e.target.value) }} /></div>
                <div><label className="form-label">Till</label><input className="form-control" type="date" value={till} min={fran || idagLokal()} onChange={e => setTill(e.target.value)} /></div>
              </div>
              {ledighetFel && (
                <div style={{ color: '#c62828', fontSize: '.78rem', margin: '4px 0 8px' }}>{ledighetFel}</div>
              )}
              <div className="checkbox-row">
                <input
                  type="checkbox"
                  id="foraldraledighet-check"
                  checked={arForaldraledighet}
                  onChange={e => setArForaldraledighet(e.target.checked)}
                />
                <label htmlFor="foraldraledighet-check" style={{ cursor: 'pointer' }}>👶 Detta gäller föräldraledighet</label>
              </div>
            </>
          )}

          {typ === 'atf' && (
            <>
              <div className="info-box info-box-blue">
                Pass: {mittPass?.passtyp_namn} · {new Date(fran).toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'long' })}
              </div>
              <div className="info-box info-box-yellow" style={{ fontSize: '.78rem' }}>
                <strong>Obs!</strong> Ledighet för heldagar ansöks via ledighetsansökan.
              </div>
              <div className="form-row">
                <div>
                  <label className="form-label">Från</label>
                  <input
                    className="form-control"
                    type="text"
                    inputMode="numeric"
                    placeholder="--:--"
                    maxLength={5}
                    value={atfFran}
                    onChange={e => setAtfFran(maskaTid(e.target.value))}
                  />
                </div>
                <div>
                  <label className="form-label">Till</label>
                  <input
                    className="form-control"
                    type="text"
                    inputMode="numeric"
                    placeholder="--:--"
                    maxLength={5}
                    value={atfTill}
                    onChange={e => setAtfTill(maskaTid(e.target.value))}
                  />
                </div>
              </div>
              {atfFelVisas && atfFel && (
                <p style={{ margin: '-4px 0 10px', fontSize: '.78rem', color: '#c62828', fontWeight: 600 }}>{atfFel}</p>
              )}
            </>
          )}

          {typ === 'passbyte' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.82rem', fontWeight: 600, marginBottom: 10, cursor: 'pointer' }}>
              <input type="checkbox" checked={flerLage}
                onChange={e => { setFlerLage(e.target.checked); setMinaValda([]); setKollegaValda([]); setKollegaDatum(null) }} />
              Passbytet gäller flera pass
            </label>
          )}

          {typ === 'passbyte' && flerLage && (
            <>
              <label className="form-label">1. Välj dina pass som du byter bort</label>
              <KollegaSchema
                kollega={medarbetareId}
                allaPass={allaPass}
                pass={pass}
                kollegaManad={minManad}
                setKollegaManad={setMinManad}
                kollegaDatum={null}
                setKollegaDatum={() => {}}
                flerval={minaValda}
                onToggle={bytMinDag}
                visaLedigaDagar={false}
                tidigastDatum={idagLokal()}
              />

              {minaValda.length > 0 && (
                <>
                  <label className="form-label" style={{ marginTop: 8 }}>2. Välj kollega att byta med</label>
                  <select className="form-control" value={valdKollega} onChange={e => { setValdKollega(e.target.value); setKollegaValda([]) }}>
                    <option value="">— Välj kollega —</option>
                    {kollegor.map(k => (
                      <option key={k.id} value={k.id}>{k.fornamn} {k.efternamn}</option>
                    ))}
                  </select>
                </>
              )}

              {minaValda.length > 0 && valdKollega && (
                <>
                  <label className="form-label" style={{ marginTop: 8 }}>
                    {behov === 0 ? '3. Kollegans pass byts samma dag' : `3. Välj ${behov} pass hos kollegan (${kollegaValda.length} av ${behov} valda)`}
                  </label>
                  <KollegaSchema
                    kollega={valdKollega}
                    allaPass={allaPass}
                    pass={pass}
                    kollegaManad={kollegaManad}
                    setKollegaManad={setKollegaManad}
                    kollegaDatum={null}
                    setKollegaDatum={() => {}}
                    flerval={[...kollegaValda, ...tvingade]}
                    lasta={tvingade}
                    onToggle={bytKollegaDag}
                    visaLedigaDagar={false}
                    tidigastDatum={idagLokal()}
                    blockerade={[...minaJobbDatum, ...ansoktLedigt]}
                  />
                  {tvingade.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.72rem', color: '#777', marginBottom: 6 }}>
                      <span style={{ width: 12, height: 12, borderRadius: 3, background: '#ffcc80', border: '1.5px solid #fb8c00', display: 'inline-block' }} />
                      Byts samma dag (kollegan jobbar också)
                    </div>
                  )}
                  {kollegaValda.length === behov && (() => {
                    const kollegaObj = allaMedarbetare.find(m => m.id === valdKollega)
                    const kFornamn = kollegaObj?.fornamn || 'Kollegan'
                    const tidTxt = pp => (pp?.passtyp?.starttid && pp?.passtyp?.sluttid) ? `${pp.passtyp.starttid.substring(0, 5)}–${pp.passtyp.sluttid.substring(0, 5)}` : ''
                    const Rad = ({ datum, pp }) => (
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '4px 0', borderBottom: '1px solid #d6ecd8' }}>
                        <span style={{ fontWeight: 700, minWidth: 74 }}>{kortDatum(datum)}</span>
                        <span style={{ fontWeight: 800, color: '#F11C0A', minWidth: 28 }}>{pp?.passtyp_namn || ''}</span>
                        <span style={{ color: '#777', fontSize: '.76rem' }}>{tidTxt(pp)}</span>
                      </div>
                    )
                    return (
                      <div style={{ background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 6, padding: '10px 12px', marginTop: 8, fontSize: '.82rem' }}>
                        <div style={{ fontWeight: 800, marginBottom: 6 }}>Sammanfattning: {minaAlla.length} pass mot {kollegaAlla.length} pass</div>
                        <div style={{ fontSize: '.7rem', fontWeight: 700, color: '#2e7d32', letterSpacing: '.4px', margin: '6px 0 2px' }}>{kFornamn.toUpperCase()} TAR DINA PASS</div>
                        {minaAlla.map(d => <Rad key={d} datum={d} pp={minaJobb.find(x => x.datum === d)} />)}
                        <div style={{ fontSize: '.7rem', fontWeight: 700, color: '#2e7d32', letterSpacing: '.4px', margin: '10px 0 2px' }}>DU TAR {agsNamn(kFornamn).toUpperCase()} PASS</div>
                        {kollegaAlla.map(d => <Rad key={d} datum={d} pp={kollegaJobb.find(x => x.datum === d)} />)}
                      </div>
                    )
                  })()}
                </>
              )}
            </>
          )}

          {typ === 'passbyte' && !flerLage && (
            <>
              {/* Mitt pass denna dag */}
              {mittPass ? (
                <div style={{ background: '#f5f5f5', borderRadius: 5, padding: '8px 10px', marginBottom: 8, fontSize: '.82rem' }}>
                  <span style={{ color: '#aaa' }}>Ditt pass {fran}: </span>
                  <strong>{mittPass.passtyp_namn}</strong>
                </div>
              ) : (
                <div style={{ background: '#ffebee', border: '1px solid #ef9a9a', borderRadius: 5, padding: '8px 10px', marginBottom: 8, fontSize: '.82rem', color: '#c62828' }}>
                  ⚠️ Du har inget pass {fran} – du kan bara byta bort dagar du jobbar
                </div>
              )}

              <label className="form-label">Välj kollega att byta med</label>
              <select className="form-control" value={valdKollega} onChange={e => { setValdKollega(e.target.value); setKollegaValda([]); setKollegaDatum(null) }}>
                <option value="">— Välj kollega —</option>
                {kollegor.map(k => (
                  <option key={k.id} value={k.id}>{k.fornamn} {k.efternamn}</option>
                ))}
              </select>

              {valdKollega && mittPass && (
                <>
                  <label className="form-label" style={{ marginTop: 8 }}>Välj dag för kollegan</label>
                  {allaPass.length === 0 && <div style={{color:'#aaa',fontSize:'.8rem'}}>Laddar schema...</div>}
                  {allaPass.length > 0 && (
                    <KollegaSchema
                      kollega={valdKollega}
                      allaPass={allaPass}
                      pass={pass}
                      kollegaManad={kollegaManad}
                      setKollegaManad={setKollegaManad}
                      kollegaDatum={kollegaDatum}
                      setKollegaDatum={setKollegaDatum}
                      visaLedigaDagar={false}
                      blockerade={[...ansoktLedigt, ...(() => {
                        const kollegaJobbarFran = allaPass.find(p => {
                          const n = (p.passtyp_namn||'').toUpperCase()
                          return p.medarbetare_id === valdKollega && p.datum === fran &&
                            n && !['X','SX','LX','SJX','V'].includes(n) && !n.startsWith('L') && !n.startsWith('S')
                        })
                        
                        if (kollegaJobbarFran) {
                          // Kollegan jobbar fran → bara samma dag tillåten
                          // Blockera alla kollegans andra jobbdagar
                          return allaPass
                            .filter(p => {
                              if (p.medarbetare_id !== valdKollega) return false
                              if (p.datum === fran) return false
                              const n = (p.passtyp_namn||'').toUpperCase()
                              return n && !['X','SX','LX','SJX','V'].includes(n) && !n.startsWith('L') && !n.startsWith('S')
                            })
                            .map(p => p.datum)
                        } else {
                          // Kollegan ledig fran → blockera dagar JAG jobbar (dubbla pass)
                          return pass.filter(p => {
                            const n = (p.passtyp_namn||'').toUpperCase()
                            return p.datum !== fran && n && !['X','SX','LX','SJX','V'].includes(n) && !n.startsWith('L') && !n.startsWith('S')
                          }).map(p => p.datum)
                        }
                      })()]}
                    />
                  )}

                  {/* Bekräftelsetext */}
                  {kollegaDatum && kollegansPass && (() => {
                    const kollega = allaMedarbetare.find(m => m.id === valdKollega)
                    const kNamn = kollega ? `${kollega.fornamn} ${kollega.efternamn}` : 'Kollegan'
                    return (
                      <div style={{ background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 5, padding: '8px 10px', marginTop: 6, fontSize: '.82rem' }}>
                        <div>✅ <strong>{kNamn}</strong> jobbar ditt {mittPass.passtyp_namn} ({fran})</div>
                        <div>✅ <strong>Du</strong> jobbar {kollegansPass.passtyp_namn} åt {kNamn} ({kollegaDatum})</div>
                      </div>
                    )
                  })()}

                </>
              )}
            </>
          )}

          {typ === 'flytta_pass' && (
            <>
              <div className="info-box info-box-blue">
                Du flyttar: D · {new Date(fran + 'T12:00:00').toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'long' })}
              </div>
              <div className="info-box info-box-yellow" style={{ fontSize: '.78rem' }}>
                Flytt av D-dag sker på egen begäran.
              </div>
              <label className="form-label">Välj ny dag (bara lediga dagar kan väljas)</label>
              {flyttPass === null ? (
                <div style={{ color: '#aaa', fontSize: '.8rem' }}>Laddar schema...</div>
              ) : (
                <KollegaSchema
                  kollega={medarbetareId}
                  allaPass={flyttPass}
                  pass={[]}
                  kollegaManad={kollegaManad}
                  setKollegaManad={setKollegaManad}
                  kollegaDatum={kollegaDatum}
                  setKollegaDatum={setKollegaDatum}
                  visaLedigaDagar={true}
                  tidigastDatum={idagLokal()}
                  tomtLage
                  blockerade={[
                    fran,
                    ...flyttPass
                      .filter(p => {
                        const n = (p.passtyp_namn || '').toUpperCase().trim()
                        if (n === '') return false
                        // Ledig dag med semester (S/SS) går bra, även om den ligger i en ledighetsansökan:
                        // man är ledig från början, så ingen semesterdag tas ut
                        if (n === 'S' || n === 'SS') return false
                        return true
                      })
                      .map(p => p.datum)
                  ]}
                />
              )}
            </>
          )}

          {typ === 'boka_dag' && (
            <>
              <label className="form-label">Datum</label>
              <input className="form-control" type="date" value={fran} onChange={e => setFran(e.target.value)} />
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, margin: '14px 0 22px', fontSize: '.85rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={onskaD} onChange={e => setOnskaD(e.target.checked)} style={{ marginTop: 3 }} />
                <span>Jag önskar D-dag denna dag</span>
              </label>
            </>
          )}

          <label className="form-label">
            Kommentar {(typ === 'boka_dag' || typ === 'flytta_pass') && <span style={{ color: '#F11C0A' }}>*obligatorisk</span>}
          </label>
          <textarea
            className="form-control"
            rows={3}
            placeholder={typ === 'boka_dag' ? 'Anledning till att du vill boka upp dagen...' : typ === 'flytta_pass' ? 'Anledning till att du vill flytta din D-dag...' : 'Valfri kommentar...'}
            value={kommentar}
            onChange={e => setKommentar(e.target.value)}
            style={(typ === 'boka_dag' || typ === 'flytta_pass') ? { borderColor: '#ffcc80' } : {}}
          />
          {typ === 'boka_dag' && <div className="info-box info-box-yellow">Kommentar är obligatorisk för bokad dag.</div>}
          {ansokFel && (
            <p style={{ margin: '8px 0 0', fontSize: '.78rem', color: '#c62828', fontWeight: 600 }}>{ansokFel}</p>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Avbryt</button>
          <button className="btn btn-primary" onClick={skicka} disabled={skickar || !!ledighetFel || (typ === 'passbyte' && (flerLage ? !flerKlart : (!mittPass || !kollegaDatum || !kollegansPass))) || (typ === 'flytta_pass' && (!kollegaDatum || !kommentar.trim()))}>
            {skickar ? 'Skickar...' : 'Skicka till admin'}
          </button>
        </div>
      </div>
    </div>
  )
}
